/**
 * Normalizer + model for championship records — rebuilt against the REAL SR
 * Championship Command Center (HubSpot custom object 2-64425303).
 *
 * The real app reads a rich property set (championships.js `PROPERTIES`) and
 * drives the map/board off two axes per record:
 *   - `sr_pursuit_status` — the sales-pursuit axis (Not Started → … → Won →
 *     Champion Crowned). Map fill = a state's DOMINANT pursuit status.
 *   - `sr_champion_status` — the outcome axis (Scheduled → In Progress →
 *     Champion Crowned → Workflow Fired). "Action Needed" = crowned records
 *     whose ring/outbound workflow hasn't fired.
 *
 * THE GEO FIX (kept — it is an improvement over the shipped app): the live app
 * keys geography off `sr_state` alone, which is only ~3.3% populated (why the
 * board can look empty). We use `sr_state` else `sr_state_location`, mapped to
 * a USPS code; USA / Canada / blank → National (`NATIONAL`).
 *
 * BACKEND PROPERTY SET (verified live 2026-07-31): `GET /api/championships`
 * returns the FULL property set — `sr_pursuit_status`, `sr_state`,
 * `sr_state_location`, `sr_champion_status`, `current_champion`, the rep
 * fields, windows, dates, etc. — flattened onto each record as `{ id, ...props }`,
 * with `paging` carrying the next cursor string. Confirmed by paging the entire
 * object: 12,740 records; sr_state 3.1% vs sr_state_location 68.4% (the geo split
 * this fix addresses); raw sr_event_date skewed (7,586 read 2027) — reconciled to
 * effective dates below. This normalizer still tolerates missing fields so it
 * degrades gracefully if the backend DTO changes.
 */

import { titleTierFor } from './titleTierView.js'
import { toUspsCode, NATIONAL } from './usStates.js'
/* The champion-of-record rule, imported rather than reimplemented so the
 * board, the dossier, the Trade Desk and every export cannot hold different
 * opinions about it. See championDisplay.js. */
import { titleTypeOf, schoolApplies, athletesOf, championSchools, meetsContract,
  isMultiSchool, schoolGroups } from './championDisplay.js'

/* ---- The 7/30/60/90 rolling lens (single source of truth) -----------
 * Every timing bucket in this module is a ROLLING window measured in days
 * from TODAY off each record's EFFECTIVE date. Nothing is bucketed by status:
 * a crowned record whose event was two years ago belongs in "Earlier", never
 * in "Just Concluded". `LOOKBACK_DAYS` / `LOOKAHEAD_DAYS` bound the lens;
 * anything outside falls to the clearly-labelled Earlier / Later buckets,
 * which are still fully browsable.
 *
 * WINDOW_STEPS drives everything downstream — the bucket edges, the cumulative
 * pill bar, and `windowCounts()`. Adding or removing a step here is the ONLY
 * change needed to reshape the lens; nothing re-derives the edges by hand.
 */
export const WINDOW_STEPS = [7, 30, 60, 90]
export const LOOKBACK_DAYS = WINDOW_STEPS[WINDOW_STEPS.length - 1]  // 90
export const LOOKAHEAD_DAYS = WINDOW_STEPS[WINDOW_STEPS.length - 1] // 90

const props = (r) => (r && typeof r.properties === 'object' ? r.properties : {})

function field(record, keys) {
  const p = props(record)
  for (const k of keys) {
    if (record?.[k] !== undefined && record[k] !== null && record[k] !== '')
      return record[k]
    if (p?.[k] !== undefined && p[k] !== null && p[k] !== '') return p[k]
  }
  return undefined
}

/**
 * HubSpot dates: epoch ms (number or numeric string) or ISO/`YYYY-MM-DD`.
 * Epoch-null sentinels (0 / near-1970) are treated as NO DATE — ~1,241 records
 * carry these and must surface as gaps, not as 1970 events.
 */
export function parseDate(value) {
  if (value == null || value === '') return null
  let d
  if (typeof value === 'number') d = new Date(value)
  else {
    const s = String(value).trim()
    if (/^\d{10,}$/.test(s)) d = new Date(Number(s))
    else d = new Date(s.length === 10 ? s + 'T00:00:00' : s)
  }
  if (Number.isNaN(d.getTime())) return null
  if (d.getUTCFullYear() <= 1971) return null // epoch-null sentinel → no date
  return d
}

/* ---- Effective-date derivation ------------------------------------------
   Source event dates are unreliable (thousands mis-dated 2027, ~1,241
   epoch-null, ~8,814 disagree with season_year). So timing keys off an
   EFFECTIVE date: the real event_date when it's sane (and agrees with
   season_year), else a date DERIVED from season_year + the sport's typical
   championship window. Each record is flagged confirmed vs estimated — honest,
   and NOT written back to the source. */

// Sport → typical championship window [monthIndex 0-11, day]. Keyword-matched.
const SPORT_WINDOWS = [
  [/football/, [10, 25]],            // late November (finals season)
  [/basketball/, [2, 12]],           // March
  [/baseball/, [5, 8]],              // June
  [/softball/, [4, 28]],             // late May
  [/volleyball/, [10, 18]],          // November
  [/field hockey/, [10, 8]],         // November
  [/soccer|futbol/, [10, 12]],       // November
  [/wrestl/, [1, 20]],               // February
  [/cross ?country/, [10, 5]],       // November
  [/track|field/, [4, 25]],          // May
  [/swim|dive|aquatic/, [1, 22]],    // February
  [/tennis/, [4, 18]],               // May
  [/golf/, [9, 12]],                 // October
  [/lacrosse/, [4, 30]],             // late May
  [/hockey/, [2, 5]],                // early March
  [/gymnastic/, [3, 18]],            // April
  [/cheer|dance|spirit/, [1, 10]],   // February
  [/bowl/, [1, 15]],                 // February
  [/water ?polo/, [10, 15]],         // November
  [/rugby/, [4, 20]],                // May
  [/flag/, [10, 20]],                // November
]
const DEFAULT_WINDOW = [4, 15] // generic mid-May championship

function sportWindow(sport) {
  const s = String(sport || '').toLowerCase()
  for (const [re, w] of SPORT_WINDOWS) if (re.test(s)) return w
  return DEFAULT_WINDOW
}
// Kept in lockstep with the 90-day look-back lens below: an estimated date is
// left in place for the FULL look-back window (so Last-30/60/90 are coherent)
// and only rolls forward once it falls outside it.
const STALE_PAST_DAYS = LOOKBACK_DAYS
/**
 * Derived championship date. Because a championship recurs, an ESTIMATED date
 * that has already passed (more than the look-back window ago) is rolled
 * forward to its NEXT occurrence — that's the rep's actionable target for an
 * open pursuit. Anything inside the look-back window is left in place so it
 * lands in the correct Last-30 / Last-60 / Last-90 bucket.
 */
function deriveSeasonDate(seasonYear, sport, now = new Date()) {
  const [m, d] = sportWindow(sport)
  // Season 2026 plus a March window is March 2027, not March 2026. Built in
  // the wrong calendar year, every spring sport landed a full year in the past.
  let date = new Date(calendarYearFor(seasonYear, m), m, d)
  const floor = new Date(now.getFullYear(), now.getMonth(), now.getDate() - STALE_PAST_DAYS)
  let guard = 0
  while (date < floor && guard < 8) { date = new Date(date.getFullYear() + 1, m, d); guard++ }
  return date
}
/* ---- A SEASON IS A WINDOW, NOT A CALENDAR YEAR -------------------------
 * SR files a season by the year it STARTS: the 2026-27 season is 2026 and it
 * ends in spring 2027. Measured on portal 51263144, 2026-09-21, across the
 * 11,382 records holding both a season year and an event date:
 *
 *   3,356  calendar year matches the season year
 *   7,513  inside Jul(Y)..Jun(Y+1), calendar year Y+1
 *     513  agrees with neither
 *
 * The note at the head of this file calls those "~8,814 disagree with
 * season_year". They do not disagree. Two thirds of them are spring finals
 * behaving exactly as SR files them.
 *
 * This rule has never once fired in production, because the snapshot route
 * drops sr_season_year and this file therefore always saw null. The backend
 * release that starts projecting the field turns it on against 14,947
 * populated values at once, which is why this ships FIRST: with the field
 * still absent the new code is inert, and the board cannot break in the gap
 * between the two deploys.
 *
 * Kept in lockstep with SEASON_START_MONTH in backtest.js on the server. If
 * these drift, the board shows a rep one day and the sweep works another.
 * ---------------------------------------------------------------------- */
export const SEASON_START_MONTH = 6   // July, 0-indexed

/** The season year a calendar date belongs to. Jul..Dec is Y; Jan..Jun is Y-1. */
export function seasonYearOf(d) {
  if (!d || isNaN(d.getTime())) return null
  return d.getMonth() >= SEASON_START_MONTH ? d.getFullYear() : d.getFullYear() - 1
}

/** The calendar year a sport-window month falls in, for a given season year. */
function calendarYearFor(seasonYear, month) {
  return month >= SEASON_START_MONTH ? seasonYear : seasonYear + 1
}

/** Does this date belong to the season the record claims? */
function agreesWithSeason(dateObj, seasonYear) {
  return !seasonYear || seasonYearOf(dateObj) === seasonYear
}

/* ---- WHICH SEASON ARE WE IN, AND WHICH SEASON IS A RECORD IN? ----------
 *
 * 🔴 THE DEFECT THESE EXIST FOR, AND IT IS A REPEAT. Brian: *"When I currently
 * look at my work view it brings up stale opportunities from 2024. Those are not
 * relevant and are completely spoken for."* He raised the same class of thing
 * before about the production attention queue — *"it shows 12 items need
 * attention, they are years old"* — and the fix there plainly did not cover this
 * view.
 *
 * ⚠️ AND THE WORKLIST PUT THEM AT THE TOP, NOT THE BOTTOM. `tierOf()` reads
 * `hasChampion(c) && !isWorked(c)` as tier 1, JUST_CROWNED — *"this is the
 * revenue moment and it decays fast."* A 2024 championship with a champion
 * recorded and no outreach logged satisfies that exactly, so two-year-old
 * records were not merely present, they were the FIRST thing a rep saw. Sorting
 * them down would not have fixed it; they are not work.
 *
 * SR files a season by its START year, Jul(Y) → Jun(Y+1), which `seasonYearOf`
 * above already implements and which is kept in lockstep with the server's
 * backtest. These two functions just name the current one and a record's one.
 */

/** The season year we are in right now. 2026-10-04 → 2026 (the 2026-2027 season). */
export function currentSeasonYear(now = new Date()) {
  return seasonYearOf(now)
}

/** "2026-2027" — the label a human recognises, never a bare year. */
export function seasonLabel(seasonYear) {
  return seasonYear == null ? '' : `${seasonYear}-${seasonYear + 1}`
}

/**
 * The season a RECORD belongs to, or null when nothing on it can say.
 *
 * ⚠️ NULL IS NOT "OLD", AND THAT DISTINCTION IS THE WHOLE SAFETY OF THIS
 * CHANGE. A date is the only thing that places a record in a season. The object
 * is full of records with no date at all — Jennifer Loveday-Donovan's cheer
 * records carry none — so a rule that treated "no season" as "prior season"
 * would empty an entire rep's worklist in the name of removing stale rows. That
 * would be a far worse defect than the one being fixed.
 *
 * So: the effective date decides; failing that the record's own declared season
 * year; failing both, null, which callers must read as "undetermined, keep it".
 */
export function recordSeasonYear(c) {
  const d = c?.effDate || c?.heldDate || c?.dateObj || null
  const fromDate = d instanceof Date ? seasonYearOf(d) : null
  if (fromDate != null) return fromDate
  const declared = Number(c?.seasonYear)
  return Number.isFinite(declared) && declared > 2000 && declared < 2100 ? declared : null
}

/**
 * Is this record from a season that has already finished?
 *
 * ⚠️ ONLY A DATED RECORD CAN BE OUT OF SEASON (see `recordSeasonYear`), and only
 * a STRICTLY EARLIER season counts. A future season is work that has not arrived
 * yet, not stale work — excluding it would hide next year's championships, which
 * is the opposite of the ask.
 */
export function isPriorSeason(c, now = new Date()) {
  const s = recordSeasonYear(c)
  return s != null && s < currentSeasonYear(now)
}

/* ---- A SPECIFIC DAY, AND A WINDOW OF DAYS ------------------------------
 *
 * Brian: *"In worklist it should be able to show a specific date… A rep must be
 * able to work A SPECIFIC DAY — what do I do today, what's next week."* So the
 * date is a dimension of the work view, not a filter bolted on.
 *
 * ⚠️ THESE LIVE HERE BECAUSE THERE WERE ALREADY THREE COPIES OF `dayKey` AND I
 * WAS ABOUT TO WRITE A FOURTH.
 *
 *   features/command-center/pursuit/CountdownCalendar.jsx:14
 *   features/reviews/dispatch.js:280
 *   features/reviews/reviewCoverage.js:302
 *
 * And the copies know about each other: dispatch.js's own comment reads "that
 * is the bug fixed in the Command Center's `dayKey()` and AGAIN in
 * reviewCoverage.js". The same UTC fault — `toISOString()` converting to UTC
 * first, so west of Greenwich an evening event keys to the following day — was
 * found and fixed three separate times in three places. That is the
 * two-copies-of-one-rule fault that broke Canva drill-in (v1.36.1) and that
 * `stageTally` was written to end, except at three.
 *
 * ⚠️ THE REVIEWS PAIR IS DELIBERATELY NOT MERGED INTO THIS. `dispatch.js`
 * handles a date-only STRING as well as a timestamp, and its comment calls that
 * "the mirror image" case — different semantics, not a duplicate. Collapsing
 * them without verifying the review dispatch would be making them consistent in
 * the WRONG direction, which CLAUDE.md already warns about on `onHunted`.
 * CountdownCalendar takes a Date, same as this, so that one consolidates
 * cleanly and does. The reviews pair is named here so the next person sees
 * three, not one.
 */

/** A local-calendar day key, `YYYY-MM-DD`. LOCAL parts, never `toISOString()`. */
export function dayKey(d) {
  if (!d) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** The inverse. Built from local parts for the same reason. */
export function parseDayKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''))
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Is a record's effective date inside `[from, to]`, inclusive, by local day?
 *
 * ⚠️ AN UNDATED RECORD IS NOT "OUTSIDE THE WINDOW", IT IS UNPLACEABLE, and the
 * caller must tell those apart. Returning false for both would let a date filter
 * silently swallow the majority of some reps' work — the records with no date at
 * all — and the rep would read an empty day as a clear day. `null` is the third
 * answer and it is the one that has to be reported.
 */
export function inDayWindow(c, from, to) {
  const d = c?.effDate || c?.heldDate || c?.dateObj || null
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null
  const k = dayKey(d)
  if (from && k < dayKey(from)) return false
  if (to && k > dayKey(to)) return false
  return true
}

/** `+n` days from a date, as a new local Date at midnight. */
export function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() + Number(n || 0))
  return x
}

function toSeasonYear(v) {
  if (v == null || v === '') return null
  const n = parseInt(String(v).slice(0, 4), 10)
  return Number.isFinite(n) && n > 2000 && n < 2100 ? n : null
}

/* ---- WHICH FIELD THE DATE CAME FROM --------------------------------------
 * `dateObj` collapses sr_confirmed_date ?? sr_event_date ?? sr_projected_date
 * into one value. Until 2026-09-20 the confidence was then decided WITHOUT
 * knowing which of the three won, and the test it used was "does this date
 * agree with the season year". A record with a date and NO season year fell
 * through to `confidence: 'confirmed'`.
 *
 * Measured against the live object that day: 1,957 records carry ONLY a
 * projected date, and ZERO of them carry a season year. So every one of them
 * rendered with `dateConfidence: 'confirmed'` and `dateEstimated: false` — a
 * projection displayed as a fact, with a countdown, on the board Brian reads.
 *
 * The 1,957 share only 86 distinct dates (165 of them on 2027-01-30 alone).
 * Nothing sourced produces 86 dates for 1,957 events. They are placeholders.
 *
 * So the basis travels WITH the date. `confirmed` now has to be earned. */
export const DATE_BASIS = {
  CONFIRMED: 'confirmed',     // sr_confirmed_date
  EVENT: 'event',             // sr_event_date
  PROJECTED: 'projected',     // sr_projected_date
  SEASON_YEAR: 'season_year', // synthesized from season year + sport window
  NONE: 'none',
}

/**
 * Effective date + confidence for a record given its raw date, season year,
 * sport, and WHICH FIELD the raw date came from.
 *
 * `{ date, confidence: 'confirmed'|'projected'|'estimated'|'none', basis }`.
 *
 * A real event_date that agrees with season_year is trusted (confirmed); a
 * date that came out of sr_projected_date is NEVER trusted, whatever the
 * season year says; otherwise the date is derived from season_year + sport
 * window (estimated).
 *
 * `basis` defaults to EVENT so a caller that has not been taught the argument
 * behaves exactly as before rather than silently downgrading real dates.
 */
export function effectiveDate(dateObj, seasonYear, sport, now = new Date(), basis = DATE_BASIS.EVENT, dateStatus = '') {
  /* A PROJECTION IS NEVER CONFIRMED. Checked FIRST, deliberately outranking
   * the season-year agreement test, because that test can only ever say "this
   * date is internally consistent" — never "someone published it". */
  if (dateObj && basis === DATE_BASIS.PROJECTED) {
    return { date: dateObj, confidence: 'projected', basis: DATE_BASIS.PROJECTED }
  }

  /* ⚠️ AND THE RECORD'S OWN STATUS OUTRANKS THE FIELD IT SITS IN.
   *
   * Measured 2026-09-20: 7,446 records rendered as CONFIRMED while their own
   * sr_date_status read "Projected". 7,409 of those got their date out of
   * sr_event_date — so on those records a projection had already been written
   * into the event-date field, and reading the field name alone could never
   * catch it. Only 3,921 records in the whole object both hold a date and say
   * "Confirmed" about it.
   *
   * sr_date_status is the one place a human or an engine has positively
   * asserted what kind of date this is. When it says Projected or PENDING,
   * that assertion wins over every inference this function could make. The
   * other option values — "Confirmed", "Dated", blank — assert nothing that
   * contradicts a date, so they are left alone; this rule only ever downgrades,
   * and only on an explicit statement. */
  const said = String(dateStatus || '').trim().toLowerCase()
  if (dateObj && (said === 'projected' || said === 'pending')) {
    return { date: dateObj, confidence: 'projected', basis }
  }
  if (dateObj && agreesWithSeason(dateObj, seasonYear)) {
    return { date: dateObj, confidence: 'confirmed', basis }
  }
  /* A DATE THAT REALLY DISAGREES WITH ITS OWN SEASON YEAR IS A CONFLICT.
   * Two fields on one record contradict each other and nothing on this screen
   * knows which is wrong. Deriving a replacement would show the rep a date
   * nobody published, and the server refuses to hunt the record anyway. So the
   * rep sees the date the record actually holds, labelled as disputed.
   * Measured 2026-09-21: 541 records across the object. */
  if (dateObj && seasonYear) {
    return { date: dateObj, confidence: 'conflict', basis,
             conflict: { seasonYear, dateSeasonYear: seasonYearOf(dateObj) } }
  }
  if (seasonYear) {
    return { date: deriveSeasonDate(seasonYear, sport, now), confidence: 'estimated', basis: DATE_BASIS.SEASON_YEAR }
  }
  if (dateObj) return { date: dateObj, confidence: 'confirmed', basis }
  return { date: null, confidence: 'none', basis: DATE_BASIS.NONE }
}

/** Which raw field supplied the collapsed date. Stated once and used by both
 *  the normalizer and the tests, so they cannot drift apart. */
export function dateBasisOf({ confirmedRaw, eventRaw, projectedRaw }) {
  if (confirmedRaw != null && String(confirmedRaw).trim()) return DATE_BASIS.CONFIRMED
  if (eventRaw != null && String(eventRaw).trim()) return DATE_BASIS.EVENT
  if (projectedRaw != null && String(projectedRaw).trim()) return DATE_BASIS.PROJECTED
  return DATE_BASIS.NONE
}

/* The one place the wording lives. Same vocabulary as the Team Rooms clock —
 * "Projected" and "Confirmed" — so two surfaces cannot describe one record
 * differently. */
export const DATE_CONFIDENCE_LABEL = {
  confirmed: 'Confirmed',
  projected: 'Projected',
  estimated: 'Estimated',
  /* A record whose event date and season year contradict each other. Not
   * customer-safe and not workable, and the rep has to see which it is rather
   * than a bare date with no qualifier. */
  conflict: 'Date disputed',
  none: 'No date',
}

/** Short suffix for a date pill. Empty only for a confirmed date; a confirmed
 *  date is the only kind that appears without a qualifier. */
export function dateQualifier(c) {
  const k = c?.dateConfidence
  if (!k || k === 'confirmed') return ''
  const label = DATE_CONFIDENCE_LABEL[k]
  return ` (${label ? label.toLowerCase() : k})`
}

/** May this date be shown to a CUSTOMER as a date? Only a confirmed one.
 *  A projection or an estimate is internal working material. */
export function dateIsCustomerSafe(c) {
  return c?.dateConfidence === 'confirmed' && c?.effDate instanceof Date
}

/**
 * WHEN IT WAS HELD — the same inputs as effectiveDate, WITHOUT the roll-forward.
 *
 * These are two different questions and conflating them is why the board could
 * not answer Brian's.
 *
 *   effectiveDate → "when does this next happen"  (pursuit; rolls forward)
 *   heldDate      → "when did this one happen"    (history; never rolls)
 *
 * The roll-forward is right for a rep chasing the next occurrence. But it means
 * an estimated date can never sit more than LOOKBACK_DAYS in the past, so the
 * look-back lens was empty beyond 90 days BY CONSTRUCTION — measured 2026-08-25:
 * 8,724 estimated dates sitting in the future, only 19 records of 13,341
 * readable as older than 90 days back, while the backtest (which does not roll)
 * found 8,743 championships already held and uncrowned.
 *
 * Nothing was missing. The dates had moved, and every "look back" answered the
 * wrong question.
 *
 * For a CONFIRMED date the two are identical, so this only changes the records
 * whose date was a guess in the first place.
 */
export function heldDate(dateObj, seasonYear, sport) {
  if (dateObj && agreesWithSeason(dateObj, seasonYear)) return dateObj
  if (dateObj && seasonYear) return dateObj    // conflict: show what the record holds
  if (seasonYear) {
    const [m, d] = sportWindow(sport)
    return new Date(calendarYearFor(seasonYear, m), m, d)
  }
  return dateObj || null
}

/* ---- Pursuit + champion status models (real values/colors) --------- */

// sr_pursuit_status → color + priority (dominance ranking for map fill).
export const PURSUIT_META = {
  'Champion Crowned': { color: '#2f9e5f', priority: 6, label: 'Champion Crowned' },
  'To Pursue': { color: '#a5762a', priority: 5, label: 'To Pursue' },
  'Outreach In Progress': { color: '#3f6f9a', priority: 4, label: 'Outreach In Progress' },
  /* ⚠️ #46934a UNTIL 2.78.0, AND IT WAS INDISTINGUISHABLE FROM Champion Crowned
   * (#2f9e5f) AS A MAP FILL ON A PHONE. Both mid-lightness greens; the two
   * states they denote are adjacent in the funnel and adjacent in the legend, so
   * the one surface where the confusion matters is exactly where it happened.
   *
   * Kept in the green family on purpose — Won → Crowned is a progression, and
   * recolouring Won to an unrelated hue would lose that. Separated by LIGHTNESS
   * instead of hue, which is what survives a small screen, a dim screen and the
   * commonest forms of colour blindness. check-contrast.mjs now fails the build
   * if any two fills in this map land this close again. */
  Won: { color: '#1f5c33', priority: 3, label: 'Won' },
  'Outreach Completed': { color: '#8a5db8', priority: 2, label: 'Outreach Completed' },
  'Not Started': { color: '#8a8272', priority: 1, label: 'Not Started' },
}
export const PURSUIT_ORDER = [
  'To Pursue', 'Outreach In Progress', 'Outreach Completed', 'Won', 'Champion Crowned', 'Not Started',
]

// sr_champion_status (outcome axis).
export const CHAMPION_STATUSES = ['Scheduled', 'In Progress', 'Champion Crowned', 'Workflow Fired']

export function pursuitMeta(status) {
  return PURSUIT_META[status] || { color: 'var(--faint)', priority: 0, label: status || '—' }
}

/* ---- Sport color coding (scannable at a glance) -------------------- */
// Keyword-matched, distinct-but-editorial hues. Every surfaced championship
// carries a sport accent so the board reads by sport instantly.
const SPORT_COLORS = [
  [/football/, '#b5502e', 'Football'],
  [/basketball/, '#c97b1e', 'Basketball'],
  [/baseball/, '#2f6e8f', 'Baseball'],
  [/softball/, '#4a8f6e', 'Softball'],
  [/volleyball/, '#b0397a', 'Volleyball'],
  [/soccer|futbol/, '#3f8f4a', 'Soccer'],
  [/wrestl/, '#6e4b8f', 'Wrestling'],
  [/cross ?country/, '#8f7a2a', 'Cross Country'],
  [/field[\s-]*hockey/, '#3a5f8f', 'Field Hockey'],
  [/track|field/, '#9e8a2a', 'Track & Field'],
  [/swim|dive|aquatic/, '#2a8f9e', 'Swim & Dive'],
  [/tennis/, '#7a9e2a', 'Tennis'],
  [/golf/, '#4a7a3a', 'Golf'],
  [/lacrosse/, '#8f5a2a', 'Lacrosse'],
  [/hockey/, '#3a5f8f', 'Hockey'],
  [/gymnastic/, '#b03a5a', 'Gymnastics'],
  [/cheer|dance|spirit|stunt/, '#c0397a', 'Cheer & Dance'],
  [/bowl/, '#6e5a3a', 'Bowling'],
  [/water ?polo/, '#2a7a9e', 'Water Polo'],
  [/rugby/, '#8f4a3a', 'Rugby'],
  [/flag/, '#c98a1e', 'Flag Football'],
  [/e-?sport|gaming/, '#5a5ab0', 'Esports'],
  [/motor ?sport|racing|nascar|indycar|formula|rally|karting/, '#556070', 'Motorsports'],
  [/row(ing)?|crew/, '#2a8f8a', 'Rowing'],
  [/ski|snowboard/, '#4a7ab0', 'Skiing'],
]
const SPORT_DEFAULT = ['#6e685b', 'Other']

/** Sport → { color, label }. Unknown/blank sports get a muted neutral. */
export function sportMeta(sport) {
  const s = String(sport || '').toLowerCase()
  if (!s.trim()) return { color: SPORT_DEFAULT[0], label: 'Unspecified' }
  for (const [re, color, label] of SPORT_COLORS) if (re.test(s)) return { color, label }
  return { color: SPORT_DEFAULT[0], label: sport }
}
export function sportColor(sport) {
  return sportMeta(sport).color
}

/* ---- Contention axis: win / loss / in-contention + stage ----------- */
// A single scannable outcome read derived from pursuit + champion status.
export function contentionMeta(c) {
  const p = String(c?.pursuit || '').trim()
  const cs = String(c?.championStatus || '').trim()
  if (c?.crowned || p === 'Champion Crowned' || cs === 'Champion Crowned' || cs === 'Workflow Fired')
    return { key: 'champion', label: 'Champion', stage: cs || 'Crowned', color: '#2f9e5f' }
  // A recorded winner (current_champion / winning_school) means the outcome is
  // decided even if sr_champion_status hasn't been flipped to "Champion Crowned".
  if (c?.champion || c?.winningSchool)
    return { key: 'champion', label: 'Champion', stage: 'Winner recorded', color: '#2f9e5f' }
  if (p === 'Won') return { key: 'won', label: 'Won', stage: 'Won', color: '#1f5c33' }
  if (/lost|closed ?lost|dead/i.test(p)) return { key: 'lost', label: 'Lost', stage: p, color: '#9a3b2f' }
  if (p === 'Outreach In Progress' || p === 'Outreach Completed')
    return { key: 'contention', label: 'In Contention', stage: p, color: '#3f6f9a' }
  if (p === 'To Pursue') return { key: 'open', label: 'In Contention', stage: 'To Pursue', color: '#a5762a' }
  return { key: 'unworked', label: 'Open', stage: p || 'Not Started', color: '#8a8272' }
}

/* ---- Geo (the kept fix) -------------------------------------------- */

export function normalizeState(record) {
  const srState = field(record, ['sr_state'])
  const srLoc = field(record, ['sr_state_location'])
  const hasState = srState != null && String(srState).trim()
  const source = hasState ? 'sr_state' : 'sr_state_location'
  const rawValue = (hasState ? srState : srLoc) ?? ''
  const code = toUspsCode(rawValue)
  return {
    code: code || NATIONAL,
    source: code ? source : rawValue ? 'national' : 'blank',
    rawValue: rawValue ? String(rawValue) : '',
  }
}

/**
 * THE ONE LINE A REP READS BEFORE THEY CALL.
 *
 * Brian, 2026-08-30: "I want a summary per champion included. You are already
 * verifying, it should take another 10 seconds to get this."
 *
 * He is right that it is nearly free, and the reason is that the hunt ALREADY
 * captures every fact this needs — champion, final score, runner-up, season
 * record, venue, head coach — and refuses to crown anything without a source
 * for them. The facts have been on the record since the story layer shipped.
 * Nobody had put them in a sentence.
 *
 * ⚠️ IT COMPOSES, IT DOES NOT WRITE.
 *
 * Every clause here is a captured field, joined by punctuation. There is no
 * model call, no connective invention, and nothing that reads as narrative —
 * because the moment a summary adds a word that is not on the record, it is a
 * sentence about a real school that nobody can source. A missing field simply
 * does not appear; it is never filled, hedged, or guessed at.
 *
 * ⚠️ AND A HUMAN-WRITTEN STORY WINS. If somebody has actually written
 * `champion_story`, that is a person's sentence about a real team and it beats
 * anything assembled from fields. This only fills the silence.
 */
export function championSummary({
  champion = '', score = '', runnerUp = '', seasonRecord = '',
  venue = '', coach = '', titleLine = '', text = '',
} = {}) {
  const S = (v) => String(v ?? '').trim()
  /* A person wrote it. Nothing composed improves on that. */
  if (S(text)) return { text: S(text), source: 'written', parts: [] }
  if (!S(champion)) return { text: '', source: 'none', parts: [] }

  const parts = []
  if (S(titleLine)) parts.push(S(titleLine))

  /* "beat Riverside 3–1" reads as one fact; the score alone reads as a riddle,
   * so they are built together and the score never stands on its own. */
  if (S(runnerUp) && S(score)) parts.push(`beat ${S(runnerUp)} ${S(score)}`)
  else if (S(runnerUp)) parts.push(`beat ${S(runnerUp)}`)
  else if (S(score)) parts.push(`final ${S(score)}`)

  if (S(venue)) parts.push(`at ${S(venue)}`)
  if (S(seasonRecord)) parts.push(`${S(seasonRecord)} on the season`)
  if (S(coach)) parts.push(`head coach ${S(coach)}`)

  if (!parts.length) {
    /* The champion is known and nothing else is. Say exactly that rather than
     * padding it into a sentence that implies more was captured. */
    return { text: '', source: 'name-only', parts: [] }
  }
  const body = parts.join(' · ')
  return { text: `${S(champion)} — ${body}.`, source: 'composed', parts }
}

/* ========================================================================= *
 * CAN THIS RECORD SEED A RING?
 *
 * Brian, 2026-09-12: "The crm must be able to seed." Then the measurement that
 * forced this: of 269 crowned records, 214 carried NOTHING behind the name, and
 * a rep only discovered it in the Artwork Studio — where a build rendered the
 * literal word SURNAME because the record had no roster and no keyed type.
 *
 * ⚠️ WHY THIS IS NOT `championIssues()`.
 *
 * `championIssues()` already listed these gaps, and that is exactly why the
 * failure survived: it collapses every non-blocking gap into ONE quiet grey chip
 * reading "needs 4 details". That chip is true, unreadable, and the same colour
 * as nothing-is-wrong. It answers "what is missing" and never answers the
 * question a rep is actually about to bet a proof on — CAN I BUILD THIS? A count
 * is not a verdict, and the whole cost of this gap was paid downstream of a
 * rep who had read that chip and reasonably concluded it was cosmetic.
 *
 * ⚠️ THE WEIGHTS ARE THE BACKEND'S, DELIBERATELY COPIED.
 *
 * `sr-portal-backend/story.js#ringReadiness()` already computes this, and
 * `GET /api/championship/:id` already returns it — per record, with the same
 * thresholds. NOTHING IN THIS APP HAS EVER CALLED IT. The board reads the PAGED
 * route (`GET /api/championships`), which does not carry a readiness verdict,
 * and asking 14,813 single-record routes to colour a column is not an option.
 *
 * So the weights below MIRROR that module rather than importing it — the two
 * repos cannot import from each other (hard rule #4). Mirrored numbers drift,
 * so `verify-seedability.mjs` parses the backend's own RING_WEIGHTS when that
 * repo is checked out beside this one and fails on divergence, the same way
 * the Team Room's model test parses `lib/journey.js`. A copy with a test is a
 * mirror; a copy without one is a second source of truth.
 *
 * ⚠️ WEIGHTED, NOT COUNTED. The exact engraved wording and the roster are what
 * a ring cannot be made without; the venue is a nice-to-have. Counting them
 * equally reports a record with a venue and no title line as half-ready, which
 * is precisely the record that produced a garbled engraving.
 *
 * Pure. No fetch, no clock. Takes a NORMALIZED record.
 * ========================================================================= */

/** Mirrors `story.js#RING_WEIGHTS`. `blocks` is what a gap stops in production —
 *  phrased as the consequence, because "no roster" means nothing to a rep and
 *  "rings are personalised per player, so production cannot start" means
 *  something immediately. */
export const SEED_FIELDS = [
  { key: 'champion', weight: 3, need: 'who won',
    blocks: 'There is no champion, so there is nothing to build.' },
  { key: 'titleLine', weight: 3, need: 'the exact championship wording',
    blocks: 'The engraved title line would have to be invented. This is what produced a ring reading SURNAME.' },
  { key: 'seasonYear', weight: 2, need: 'the year',
    blocks: 'A ring carries the year. Without it the die is wrong.' },
  { key: 'seasonRecord', weight: 2, need: 'the season record',
    blocks: 'The record panel has nothing to engrave.' },
  { key: 'rosterUrl', weight: 2, need: 'where the player names are',
    blocks: 'Rings are personalised per player, so production cannot start.' },
  { key: 'score', weight: 1, need: 'the final score',
    blocks: 'No score for the result panel.' },
  { key: 'runnerUp', weight: 1, need: 'who they beat',
    blocks: 'No opponent for the result panel.' },
  { key: 'coach', weight: 1, need: 'the head coach',
    blocks: 'The presentation line has no name.' },
  { key: 'venue', weight: 1, need: 'the venue',
    blocks: 'No venue line — cosmetic, not blocking.' },
]

/** The three that genuinely stop a build rather than thin it out. */
const SEED_BLOCKING = new Set(['champion', 'titleLine', 'seasonYear', 'rosterUrl'])

/**
 * seedability(normalizedRecord) → the verdict, in the rep's words.
 *
 * `state`:
 *   `none`      no champion — not a seed problem, a hunt problem
 *   `name_only` crowned and nothing behind the name. CANNOT seed.
 *   `partial`   some of it. Can start, will need a human for the rest.
 *   `ready`     a designer can start without asking anybody a question.
 */
export function seedability(c) {
  const S = (v) => String(v ?? '').trim()
  const story = c?.story || {}
  const val = {
    champion: S(c?.champion) || S(c?.winningSchool),
    titleLine: S(story.titleLine),
    seasonYear: S(c?.seasonYear) || S(c?.season),
    seasonRecord: S(story.record),
    rosterUrl: S(story.rosterUrl),
    score: S(story.score),
    runnerUp: S(story.runnerUp),
    coach: S(story.coach),
    venue: S(story.venue),
  }

  let have = 0, total = 0
  const missing = []
  for (const f of SEED_FIELDS) {
    total += f.weight
    if (val[f.key]) have += f.weight
    else missing.push({ ...f, blocking: SEED_BLOCKING.has(f.key) })
  }
  const percent = Math.round((have / total) * 100)
  const blocking = missing.filter((m) => m.blocking)

  /* A human-written story satisfies the wording the same way a keyed title line
   * does — it is a person's sentence about a real team, and the locked layer can
   * key from it. Same precedence as championSummary(). */
  const written = !!S(story.text)

  if (!val.champion) {
    return { state: 'none', percent, missing, blocking, label: '',
      why: 'No champion recorded, so there is nothing to seed yet. That is a hunt, not a seed gap.' }
  }
  /* NAME-ONLY IS THE SEED FAILURE, AND IT GETS THE LOUD VERDICT. Nothing behind
   * the name means a proof built from this record is a template with a school
   * name on it — which is the thing that must never reach a customer. */
  if (!written && !SEED_FIELDS.some((f) => f.key !== 'champion' && f.key !== 'seasonYear' && val[f.key])) {
    return { state: 'name_only', percent, missing, blocking,
      label: 'cannot seed a ring',
      why: `Crowned, with nothing behind the name. ${blocking.map((b) => b.blocks).join(' ')} Hunt this record before anyone opens the Artwork Studio on it.` }
  }
  if (percent >= 85 && !blocking.length) {
    return { state: 'ready', percent, missing, blocking, label: '',
      why: 'Enough to build a custom design without going back to the customer.' }
  }
  return { state: 'partial', percent, missing, blocking,
    label: blocking.length
      ? `cannot seed — no ${blocking.map((b) => b.need).join(', no ')}`
      : `seeds at ${percent}%`,
    why: blocking.length
      ? blocking.map((b) => b.blocks).join(' ')
      : `Buildable, but thin: still missing ${missing.map((m) => m.need).join(', ')}.` }
}

/* ========================================================================= *
 * ONE SOURCE, MANY CHAMPIONS — AND THE QUESTION THAT ACTUALLY MATTERS
 *
 * Brian: "when we crown a championship, we have a source every time. It
 * includes a URL. That has been a rule of Command Center."
 *
 * The rule holds on PRESENCE and is already enforced: `/api/champion-resolve`
 * refuses a `crowned` outcome without an http(s) source, `championTrust()`
 * renders a champion with no source as UNSOURCED at severity `bad`, and the
 * 2026-09-12 audit measured 206 of 269 crowned records carrying a valid URL
 * with the other 63 correctly reading unsourced. Nothing here changes that.
 *
 * ⚠️ SHARING A SOURCE IS NOT A DEFECT. THIS IS A RULING, NOT AN OPINION.
 *
 * Brian, 2026-09-12, on whether a reused citation should be refused at write
 * time: **"It shouldn't be refused. It could have one source with multiple
 * champs listed."**
 *
 * He is right, and it is the normal shape of this data — a USEF release
 * crowning 22 vaulting divisions, a state association's results page carrying
 * every classification, an NCAA history table. Refusing on fan-out would block
 * the CORRECT case and still miss the real one. So nothing in this module may
 * ever become a blocker, and no write path may consult it.
 *
 * ⚠️ REUSE COUNT WAS NEVER THE SIGNAL. THE SIGNAL IS NAME-PRESENCE:
 * does the cited page name THIS record's champion?
 *
 * The audit's own USEA finding is the whole argument in one example. 19
 * American Eventing records cite ONE article whose title, headings and
 * champions are all BEGINNER NOVICE:
 *
 *   · 7 of the 19 ARE Beginner Novice records, and their 7 champions match the
 *     7 the article names, name for name. Perfectly sourced off a shared page.
 *   · the other 12 — Starter, Novice, and the Advanced Final — cite a page that
 *     structurally cannot name them.
 *
 * Same URL. Same fan-out count. Opposite verdicts. A count cannot tell those
 * two groups apart; only reading the page for the name can. That check is
 * specified in `docs/CHAMPION-SOURCE-AUDIT.md` §5 and is NOT implemented here.
 *
 * ⚠️ SO WHAT IS THIS FOR? It raises the QUESTION on the rows where it is worth
 * asking, and it phrases it as a question. It never asserts a defect, because
 * asserting one on a correct record is the exact false positive that cost this
 * audit its predecessor — and because Brian has ruled that the correct case is
 * legitimate. `warn`, never `bad`; see the assertion in verify-citation.mjs §6.
 *
 * ⚠️ IT IS ALSO NOT A SECOND TRUST VOCABULARY. It rides `championIssues()`
 * beside `seedability`'s `noseed`, reads a field stamped by the function
 * below, and does nothing at all when that field is absent — so every existing
 * caller of `championIssues(record)` is byte-identical.
 *
 * Pure. No fetch, no clock.
 * ========================================================================= */

/** Under this, the question is not worth raising — a final and its recap, a
 *  parent and its one child. Above it, one page is answering for a whole event
 *  and it is worth opening once. NOT a defect threshold: a page above it is as
 *  likely to be a legitimate multi-division results article as not. */
export const CITATION_FANOUT_MIN = 3

/* =========================================================================
 * IS THIS CROWN LOCKED BY A HUMAN?
 *
 * ⚠️ AN UNRECOGNISED VALUE READS AS LOCKED, AND THAT IS THE WHOLE POINT.
 *
 * This mirrors `sr-portal-backend/assignments.js#isLocked` clause for clause,
 * including its direction: HubSpot hands booleans back as strings, an unset
 * property arrives as `''` or absent, and anything we do not recognise is
 * treated as LOCKED — because the only safe way to misread this particular
 * field is toward refusing to act on the record.
 *
 * ⚠️ IF THIS DRIFTS FROM THAT FUNCTION, THE CONTROL STARTS LYING. The backend's
 * copy is the enforcement; this one exists so a locked record can be drawn as
 * locked instead of being discovered by pressing a button. A client reading
 * that is LOOSER than the server's would draw a live control over a record the
 * server will veto — the failure this is here to prevent.
 * ========================================================================= */
export function isChampionLocked(v) {
  if (v == null || v === '') return false
  const s = String(v).trim().toLowerCase()
  if (['false', '0', 'no', 'off', 'null', 'undefined'].includes(s)) return false
  return true
}

/** citationFanout(records) → Map<url, count> over CROWNED records only.
 *  An uncrowned record's source is where the hunt LOOKED, not what it found,
 *  and counting those would inflate every legitimate results page. */
export function citationFanout(records) {
  const m = new Map()
  for (const c of records || []) {
    if (!String(c?.champion ?? c?.winningSchool ?? '').trim()) continue
    const u = String(c?.championSourceUrl ?? '').trim()
    if (!u) continue
    m.set(u, (m.get(u) || 0) + 1)
  }
  return m
}

/** Stamps `citationShared` onto each record, the way `attachCheerRollup` does.
 *  Returns a NEW array; the inputs are not mutated. */
export function attachCitationFanout(records) {
  const m = citationFanout(records)
  return (records || []).map((c) => {
    const u = String(c?.championSourceUrl ?? '').trim()
    const n = u ? (m.get(u) || 0) : 0
    return n > 1 ? { ...c, citationShared: n } : c
  })
}

/** citationReuse(normalizedRecord) → null, or the QUESTION. Reads only the
 *  stamped count, so it is a per-record question once `attachCitationFanout`
 *  has run.
 *
 *  ⚠️ THE WORDING IS LOAD-BEARING AND IS ASSERTED. It must read as a question
 *  about this record, never as a complaint about the page. "Too many records
 *  cite this" is the wrong sentence — one source legitimately listing many
 *  champions is exactly what Brian described, and a rep who reads the flag as
 *  an accusation will either ignore it or "fix" a correct record. */
export function citationReuse(c) {
  const n = Number(c?.citationShared || 0)
  if (!String(c?.championSourceUrl ?? '').trim()) return null
  if (!(n >= CITATION_FANOUT_MIN)) return null
  return {
    key: 'sharedsource',
    count: n,
    label: 'shared source — does it name this one?',
    why: `This page is cited by ${n} crowned records. One source listing many champions is `
      + `normal and correct — a results article covering every division does exactly that. `
      + `The only question is whether it names THIS championship's champion. Open it once and `
      + `check; if it does, nothing is wrong here.`,
  }
}

/* ========================================================================= *
 * DATE INTEGRITY — is the date on this record worth believing?
 *
 * Brian, after reading the first real hand-hunt of eight concluded events:
 * "especially if a date is wrong, that will destroy credibility and we end up
 * missing it later on… and if we find a champion is being crowned days from
 * the listed date we need to know that."
 *
 * That hunt found all three failure modes in eight records, which is the whole
 * argument for measuring this rather than noticing it:
 *
 *   DRIFT     Little League World Series was listed Aug 29. The title game was
 *             played Sunday Aug 30. One day — harmless on its own, and exactly
 *             the shape that is invisible until somebody hunts by hand.
 *   WRONG     National Battle of the Bands was listed concluding in late
 *             August. It is a January event, and a showcase rather than a
 *             scored competition. No amount of searching fixes that record.
 *   MISSED    an event whose date has passed, with no champion and no search.
 *             This is the one that costs money: the window to find a result is
 *             roughly one to four weeks, and a record nobody looked at inside
 *             that window quietly becomes unfindable.
 *
 * ⚠️ SMALL DRIFT IS NOT A DEFECT, AND SAYING SO IS THE POINT.
 *
 * A final listed for Saturday that finishes Sunday is a normal championship,
 * not a data error. Flagging it red would put a warning on a large share of the
 * board and train the team to ignore every warning, including the real ones.
 * So drift inside `DRIFT_NORMAL_DAYS` is reported as INFORMATION — the date
 * moved, here is by how much — and only a wider gap is called a problem.
 *
 * Pure. No fetch, no clock beyond the `now` passed in, so it is testable and
 * gives the same answer for the same record every time.
 * ========================================================================= */

/** A final can legitimately land a couple of days off its listed date. */
export const DRIFT_NORMAL_DAYS = 3
/** Beyond this the listed date was not a scheduling slip, it was wrong. */
export const DRIFT_WRONG_DAYS = 21
/** Past this, with nobody having looked, the result is usually gone. */
export const MISSED_AFTER_DAYS = 30

const DAY = 86400000

export function dateIntegrity(c, now = new Date()) {
  const ok = { key: 'ok', severity: 'ok', label: '', why: '', driftDays: null }
  if (!c) return ok

  /* ⚠️ COMPARE THE TWO RAW FIELDS, NOT `dateObj`.
   *
   * `dateObj` is confirmed-else-event-else-projected — it has ALREADY collapsed
   * the two values this function exists to compare, so measuring drift against
   * it returns 0 on every crowned record and the check silently does nothing.
   * That is what the first version of this did. The listed date is
   * `sr_event_date`; the source's date is `sr_confirmed_date`. */
  const listed = parseDate(c.eventRaw)
  const confirmed = parseDate(c.confirmedRaw)
  const champ = String(c.champion || c.winningSchool || '').trim()
  const tag = String(c.championSearchTag || '').trim()

  /* ---- 1. DRIFT. Only computable once BOTH dates exist, which is precisely
   * when a source has told us something the record did not know. */
  if (listed && confirmed) {
    const drift = Math.round((confirmed.getTime() - listed.getTime()) / DAY)
    const mag = Math.abs(drift)
    if (mag > DRIFT_WRONG_DAYS) {
      return {
        key: 'date_wrong', severity: 'bad', driftDays: drift,
        label: `Listed date was ${mag} days out`,
        why: `The source puts this ${mag} days from the date on the record. That is not a scheduling slip — the listed date is wrong, and every countdown, window and sweep that reads it has been wrong too. Fix the record, not just this crown.`,
      }
    }
    if (mag > DRIFT_NORMAL_DAYS) {
      return {
        key: 'date_drift', severity: 'warn', driftDays: drift,
        /* ⚠️ ONLY A CROWNED RECORD WAS "CROWNED". This block sits ABOVE the
         * no-champion return on purpose (an undated miss is worth flagging
         * whether or not anyone won), but the wording did not follow: three
         * cards on Brian's phone read "Crowned 6 days before the listed date"
         * while also reading "no champion yet". A chip that contradicts the
         * field beside it is worse than no chip. */
        label: champ
          ? `Crowned ${mag} days ${drift > 0 ? 'after' : 'before'} the listed date`
          : `Source dates this ${mag} days ${drift > 0 ? 'after' : 'before'} the listed date`,
        why: `Wide enough to be worth checking. If the event genuinely moved, correct the record so next season's projection starts from the truth.`,
      }
    }
    if (mag > 0) {
      return {
        key: 'date_shifted', severity: 'info', driftDays: drift,
        label: champ
          ? `Decided ${mag} day${mag === 1 ? '' : 's'} ${drift > 0 ? 'after' : 'before'} the listed date`
          : `Source dates this ${mag} day${mag === 1 ? '' : 's'} ${drift > 0 ? 'after' : 'before'} the listed date`,
        why: 'Normal for a final — the listed date is the schedule, this is when it was actually decided. Recorded so the pattern is visible, not because anything is wrong.',
      }
    }
    return ok
  }

  /* ---- 2. MISSED. Past, uncrowned, and nobody has looked. The costly one. */
  const held = c.heldDate instanceof Date && !Number.isNaN(c.heldDate.getTime()) ? c.heldDate : listed
  if (!champ && held) {
    const age = Math.round((now.getTime() - held.getTime()) / DAY)
    const looked = Boolean(tag) || Number(c.championSearchAttempts || 0) > 0
    if (age > MISSED_AFTER_DAYS && !looked) {
      return {
        key: 'missed', severity: 'bad', driftDays: null,
        label: `${age} days past, never looked at`,
        why: `Results stay findable for roughly one to four weeks. Nobody has searched this one and the window has closed, so it will most likely need a person who was there rather than a source.`,
      }
    }
    /* An ESTIMATED date on a past record is a guess that has expired. It is not
     * evidence the event happened, and treating it as such is how a record gets
     * hunted for a result that was never contested on that date. */
    /* ⚠️ `dateGuessed`, NOT `dateEstimated`. Once a projection stopped
     * reporting itself as confirmed it also stopped being `dateEstimated`,
     * and this check would have gone quiet on exactly the 1,957 records it
     * most needs to speak up about. A narrower confidence must never buy a
     * record its way OUT of an integrity warning. */
    /* ⚠️ `dateGuessed` OR the older flags, and the OR is the point.
     *
     * This first read `c.dateEstimated`, then I narrowed it to `c.dateGuessed`
     * when a projection stopped counting as an estimate. That broke
     * verify-dates.mjs, which constructs `{ dateEstimated: true }` directly —
     * so a caller holding the OLD flag silently stopped getting the warning.
     *
     * Which is this patch's own lesson, one level up: widening a concept must
     * never narrow it for anyone still speaking the previous vocabulary. Every
     * flag that has ever meant "this date is a guess" is honoured here. */
    if (age > 0 && (c.dateGuessed || c.dateEstimated || c.dateProjected) && !looked) {
      return {
        key: 'date_guessed', severity: 'warn', driftDays: null,
        label: c.dateProjected ? 'Past a projected date' : 'Past an estimated date',
        why: c.dateProjected
          ? 'This date is a projection — a placeholder, not a published calendar entry. It has passed, which tells us nothing. Confirm the real date before treating this as concluded.'
          : 'This date was derived from the season and the sport, not stated by anyone. It has passed, which tells us nothing — confirm the real date before treating this as concluded.',
      }
    }
  }

  return ok
}

/**
 * A RETIRED PHANTOM — a record kept for history that can never be crowned.
 *
 * ⚠️ ONE PREDICATE, EVERY VIEW. The `RETIRED - ` prefix test already existed in
 * TWO places (`championshipReadiness.js`, `championshipAudit.js`), which is why
 * Readiness could report "33 RETIRED — CANNOT BE CROWNED" while Gaps offered
 * `RETIRED - Atlantic Coast Conference Men's Water Polo Championship` as a
 * workable row carrying "Suggested rep: Philip Davanzo III". One view knew the
 * record was dead and the others proposed a rep and offered a hunt on it.
 *
 * Retiring is a WRITE and is not done from here. This only reads the name the
 * retirement already wrote, and is stamped onto the normalized record so no
 * view has to know the spelling.
 */
export function isRetired(c) {
  const n = String(c?.name ?? c?.championship_name ?? c?.raw?.championship_name ?? '')
  return /^RETIRED\s*-\s*/i.test(n)
}

/**
 * ⭐ RULED OUT OF SCOPE — the same ONE-PREDICATE-EVERY-VIEW discipline as
 * `isRetired` above, and for the same reason it was written.
 *
 * `sr_scope_status` was created on 2-64425303 on 2026-10-16 (Brian: "add a
 * proper scope field as long as it doesn't collide"). Before it existed, the
 * only way to exclude a title SR does not sell into was to ARCHIVE the
 * record, which removes it from every count including the ones that should
 * still see it.
 *
 * ⚠️ OUT OF SCOPE IS NOT A GAP AND IT IS NOT AN HONEST ZERO. Those are the
 * two wrong homes the scope rules name explicitly: logged as a gap the title
 * re-enters the creation queue every wave; logged as a zero it libels a body
 * that does crown it. It is a third thing — SR's scope is narrower than the
 * body's slate — and it has to be excluded from denominators WITHOUT being
 * counted against anybody.
 *
 * ⚠️ BLANK IS NOT "IN SCOPE". A record nobody has ruled on reads blank, which
 * is a real and different state from a recorded In Scope. This predicate is
 * true ONLY for an explicit Out of Scope, so an unruled record keeps counting
 * — which is the correct direction to be wrong in.
 */
export function isOutOfScope(c) {
  const raw = c?.raw || c || {}
  return String(raw.sr_scope_status ?? c?.scopeStatus ?? '').trim() === 'Out of Scope'
}

/** The ruling's reason, so a surface can say WHY rather than just that it is. */
export function scopeReasonOf(c) {
  const raw = c?.raw || c || {}
  return String(raw.sr_scope_reason ?? c?.scopeReason ?? '').trim()
}

export function normalizeChampionship(record) {
  if (!record || typeof record !== 'object') return null

  const id = field(record, ['id', 'hs_object_id', 'objectId', 'recordId'])
  const name =
    field(record, ['sr_championship_name', 'championship_name', 'name', 'title', 'event_name']) ||
    'Untitled Championship'

  // Effective date = confirmed, else event, else projected (real app logic).
  const confirmedRaw = field(record, ['sr_confirmed_date'])
  const eventRaw = field(record, ['sr_event_date', 'event_date', 'date'])
  const projectedRaw = field(record, ['sr_projected_date'])
  const dateRaw = confirmedRaw ?? eventRaw ?? projectedRaw
  const dateObj = parseDate(dateRaw)

  const dateStatus =
    field(record, ['sr_date_status']) ||
    (confirmedRaw ? 'Confirmed' : projectedRaw ? 'Projected' : 'TBD')

  const geo = normalizeState(record)
  const pursuit = field(record, ['sr_pursuit_status']) || 'Not Started'
  const championStatus = field(record, ['sr_champion_status']) || 'Scheduled'
  const sport = field(record, ['sr_sport', 'sport', 'sr_category']) || ''
  const seasonYear = toSeasonYear(field(record, ['sr_season_year', 'season', 'sr_year', 'year']))
  /* WHICH field won the collapse above decides whether this date may be called
   * confirmed. Without it, a projection with no season year reads as fact. */
  const basis = dateBasisOf({ confirmedRaw, eventRaw, projectedRaw })
  const eff = effectiveDate(dateObj, seasonYear, sport, undefined, basis, dateStatus)

  return {
    ...titleTierFor(record),
    id: id != null ? String(id) : `${name}-${dateRaw || ''}`,
    schoolTeamCoverage: record.schoolTeamCoverage || null,
    name: String(name),
    dateObj,                 // raw event date (provenance)
    effDate: eff.date,       // NEXT occurrence — pursuit, countdowns, look-forward
    heldDate: heldDate(dateObj, seasonYear, sport), // THIS occurrence — look-back
    dateConfidence: eff.confidence, // 'confirmed' | 'projected' | 'estimated' | 'none'
    dateBasis: eff.basis,           // which raw field supplied it
    dateEstimated: eff.confidence === 'estimated',
    dateProjected: eff.confidence === 'projected',
    /* THE ONE PREDICATE anything customer-facing must ask before printing a
     * date. Not `effDate != null` — that is true for a placeholder too. */
    dateTrusted: eff.confidence === 'confirmed',
    /* A guess of ANY kind. `dateEstimated` alone no longer covers it, and the
     * integrity check below depends on this distinction. */
    dateGuessed: eff.confidence === 'estimated' || eff.confidence === 'projected',
    seasonYear,
    dateRaw: dateRaw != null ? String(dateRaw) : '',
    /* Kept RAW and separate from `dateObj` so date drift is computable at all:
     * `dateObj` collapses confirmed-else-event-else-projected into one value,
     * which is exactly the information dateIntegrity() needs both halves of. */
    confirmedRaw: confirmedRaw != null ? String(confirmedRaw) : '',
    eventRaw: eventRaw != null ? String(eventRaw) : '',
    dateStatus,
    dateLocked: dateStatus === 'Confirmed',

    // geo — group/color/filter on `state`
    state: geo.code,
    stateSource: geo.source,
    stateRaw: geo.rawValue,

    // pursuit + outcome axes
    pursuit,
    championStatus,
    /* CROWNED MEANS THERE IS A CHAMPION.
     *
     * This used to test `sr_champion_status` alone, which produced the exact
     * contradiction Brian caught on the Arena Crown record: a panel headed
     * "Nashville Kats" and captioned "Not yet crowned".
     *
     * `sr_champion_status` is an AUTOMATION flag — the resolution path
     * deliberately never writes it, because flipping it fires ring-artwork and
     * outbound. So every champion written by /api/champion-resolve reads as
     * uncrowned here. The status field says "has the workflow run"; it was
     * being asked "did somebody win", and those are different questions.
     *
     * `current_champion` is the answer to the second one. */
    crowned: !!field(record, ['current_champion']) ||
      championStatus === 'Champion Crowned' || championStatus === 'Workflow Fired',
    workflowFired: championStatus === 'Workflow Fired',
    // The detection engine raises this when a crowned champion is ready for the
    // artwork assembly to auto-fire. Falls back to `crowned` until that lands.
    readyForArtwork: titleTierFor(record).ringArtworkEligible && (
      !!field(record, ['sr_ready_for_artwork', 'ready_for_artwork']) ||
      !!field(record, ['current_champion']) ||
      championStatus === 'Champion Crowned' || championStatus === 'Workflow Fired'),
    ringFlag: field(record, ['sr_ring_opportunity_flag']) || null,

    // descriptors
    sport,
    gender: field(record, ['sr_gender']) || '',
    level: field(record, ['sr_level']) || '',
    division: field(record, ['sr_division', 'division', 'sr_class']) || '',
    conference: cleanConference(field(record, ['sr_conference'])),
    /* ⚠️ THE GOVERNING BODY — SENT SINCE FOREVER, READ NOWHERE UNTIL 2.83.0.
     * `sr_governing_body` is in the backend's CORE_PROPS and no file in src/
     * referenced it. That is the fourth instance of the same shape in six
     * releases, after `.tam`, `asOf` and `bookMeta`: the data was on the wire
     * and this client dropped it.
     *
     * It matters more than the others. A state is NOT one jurisdiction — Texas
     * is UIL and TAPPS and SPC; Florida is FHSAA, FIHSAA, SSAC and FCAPPS — and
     * the private associations are the half that gets missed. Without this field
     * the map could only ever show a state as one flat area, which is the thing
     * it was being asked to stop doing. */
    /* 🔴 2026-10-26: `sr_governing_body` DOES NOT EXIST on 2-64425303 (the
     * backend verified it live 2026-10-03 and dropped the phantom); the real
     * property is `governing_body`, on 15,050 board records. Reading only the
     * phantom left the body blank on every work row. Real name first. */
    governingBody: field(record, ['governing_body', 'sr_governing_body']) || '',
    season: field(record, ['sr_season_year', 'season', 'sr_year', 'year']) || '',
    // sr_segment is how the org categorizes the championship (HS State, College,
    // All-Star, Dance, Motorsports, …) — the "division/category" the team sees.
    segment: field(record, ['sr_segment']) || '',
    city: field(record, ['sr_city', 'city']) || '',
    region: field(record, ['sr_region']) || '',

    // people
    rep: field(record, ['sr_assigned_rep', 'sr_lead_rep']) || 'Unassigned',
    leadRep: field(record, ['sr_lead_rep']) || '',
    supportRep: field(record, ['sr_support_rep_1']) || '',

    /* THE ASSIGNMENT RECORD — why this record is this rep's, and since when.
     *
     * ⚠️ 9,774 assignments were written to production on 2026-09-30, every one
     * carrying a reason, and NOTHING read them back. The same shape as the
     * champion_* provenance bug: the backend opened these for WRITING and the
     * read list did not name them, and HubSpot returns only the properties you
     * ask for — so the reason existed on every record and could not reach a
     * single screen. The read side landed in backend 4e1625d (live in f57be91,
     * CORE_PROPS), which is what makes these normalizable at all.
     *
     * Nothing here is derived or defaulted. A rep seeing blank must be seeing a
     * record with no assignment reason, not a normalizer that dropped it. */
    supportRep2: field(record, ['sr_support_rep_2']) || '',
    assignCycle: field(record, ['sr_owner_cycle']) || '',
    assignedAt: parseDate(field(record, ['sr_owner_assigned_at'])),
    assignReason: field(record, ['sr_owner_assign_reason']) || '',
    /* A human has settled this one, so the next automated pass must not move it.
     * Surfaced because an assignment a rep cannot see is protected is an
     * assignment they will assume is up for grabs. */
    assignLocked: !!field(record, ['sr_owner_locked']),

    // winner
    champion: field(record, ['current_champion']) || '',
    winningSchool: field(record, ['sr_winning_school']) || '',
    crownedAt: parseDate(field(record, ['sr_winner_captured_date'])),

    /* 🔴 THE ATHLETES, and the title's own shape, carried on the normalized
     * record so no consumer has to derive either.
     *
     * Brian Kass, 2026-10-19: "Individual championships are NOT split. The
     * champion of record is the SCHOOL, and the individual athlete and event
     * are attached as the reference."
     *
     * ⚠️ `champion` ABOVE IS LEFT EXACTLY AS IT WAS, and under this ruling
     * that is the fix rather than a compromise. 40 `champion || winningSchool`
     * call sites across 21 files were all reaching for the ACCOUNT, and
     * `current_champion` now holds a school on every kind of title — so those
     * sites become correct without being edited. The fallback is not removed
     * by touching 40 places; it is made harmless by fixing what the field
     * means.
     *
     *   titleType   'individual' | 'team' | 'unknown', off the record's own
     *               name and key — never off what the champion value looks
     *               like, because a boat and a dressage horse both read like
     *               people and 100 of 230 individual titles are uncrowned
     *               (measured 2026-10-19).
     *   athletes    the list to render UNDER the school.
     *   athletesError  set when `champion_references` is present but
     *               unreadable. ⚠️ NOT the same as an empty list: one is a gap
     *               to fill, the other is a defect to fix, and collapsing them
     *               turns a defect into a silent zero.
     *   schools     every distinct school the crown touches — what the
     *               buying-committee step iterates.
     *   contractMet false WITH a champion present is a real finding (a crown
     *               still in the retired blob shape), not a rendering problem.
     */
    ...(() => {
      const shape = { name: field(record, ['championship_name', 'name']) || '',
        key: field(record, ['sr_championship_key']) || '',
        champion: field(record, ['current_champion']) || '',
        winningSchool: field(record, ['sr_winning_school']) || '',
        championReferences: field(record, ['champion_references']) || '',
        championSearchTag: field(record, ['sr_champion_search']) || '',
        /* §3b needs the segment: on Equestrian / Pro the athlete IS the
         * champion of record, and flagging that invents 13 defects. */
        segment: field(record, ['sr_segment']) || '' }
      const a = athletesOf(shape)
      const multi = isMultiSchool(shape)
      const g = multi ? schoolGroups(shape) : null
      return {
        titleType: titleTypeOf(shape),
        schoolApplies: schoolApplies(shape),
        athletes: a.ok ? a.lines : null,
        athletesError: a.ok ? null : a.error,
        schools: championSchools(shape),
        /* 🔴 Multi-school: no single champion is named, each school is listed
         * with its own athletes, and the row carries a CTA. A blank champion
         * here is the CORRECT state awaiting a human, never a hunt gap. */
        multiSchool: multi,
        schoolGroups: g && g.ok ? g.groups : null,
        contractMet: meetsContract(shape),
      }
    })(),

    /* THE STORY — what the source said beyond the winner's name.
     *
     * These are the inputs a CUSTOM ring is built from: the score, who they
     * beat, the season record, the venue, the coach, the exact championship
     * wording. They have existed on the record since the story layer shipped,
     * but this normalizer never carried them, so the Command Center could not
     * see them and fell back to a placeholder that read "headline record, key
     * wins, and the title-clinching moment go here" — template instruction
     * text, in a panel that composes outreach.
     *
     * Nothing here is derived or defaulted. A blank field means the source did
     * not state it, and blank is the honest answer. */
    story: {
      score: field(record, ['champion_final_score']) || '',
      runnerUp: field(record, ['champion_runner_up']) || '',
      record: field(record, ['champion_season_record']) || '',
      venue: field(record, ['champion_venue']) || '',
      coach: field(record, ['champion_head_coach']) || '',
      rosterUrl: field(record, ['champion_roster_url']) || '',
      titleLine: field(record, ['champion_title_line']) || '',
      text: field(record, ['champion_story']) || '',
      sourceUrl: field(record, ['champion_story_source', 'champion_source_url']) || '',
    },

    /* PROVENANCE — where the champion came from, so a record can show its own
     * receipts instead of asking someone to trust it. */
    championSourceUrl: field(record, ['champion_source_url']) || '',
    /* THE ONE LINE A REP READS BEFORE THEY CALL. See championSummary(). */
    summary: championSummary({
      champion: field(record, ['current_champion']) || field(record, ['sr_winning_school']) || '',
      score: field(record, ['champion_final_score']) || '',
      runnerUp: field(record, ['champion_runner_up']) || '',
      seasonRecord: field(record, ['champion_season_record']) || '',
      venue: field(record, ['champion_venue']) || '',
      coach: field(record, ['champion_head_coach']) || '',
      titleLine: field(record, ['champion_title_line']) || '',
      text: field(record, ['champion_story']) || '',
    }),
    championProvenance: field(record, ['champion_provenance']) || '',
    championEnteredBy: field(record, ['champion_entered_by']) || '',
    /* ⚠️ THE HUMAN OVERRIDE MARKER, AND IT WAS NOT BEING CARRIED THROUGH.
     *
     * `champion_locked` is in the backend's CHAIN_PROPS and has been on every
     * page this app reads since the artwork chain shipped — nothing in `src/`
     * ever looked at it. The only way this app could discover a locked record
     * was to attempt a write and read the refusal, which is exactly the shape
     * of control Brian rules against: a button that looks live, is pressed, and
     * comes back "no". A lock is a decision a person made, and it should be
     * legible on the record BEFORE anybody reaches for a control it vetoes.
     *
     * Read with `isChampionLocked()` — the same reading the backend uses, in
     * the same direction. See that function. */
    championLocked: isChampionLocked(field(record, ['champion_locked'])),
    /* THE HUMAN-READABLE ACCOUNT OF WHERE THE CHAMPION CAME FROM — and, once a
     * crown has been withdrawn, of where it WENT. `repair-defects` writes the
     * retraction narrative here rather than only to the ledger, so that a blank
     * champion reads as a DECISION and not as a gap nobody has got to yet.
     * Without it on the record, the row goes straight back into the hunt queue
     * and somebody re-crowns it from the same bad guess. */
    championDetectedRaw: field(record, ['champion_detected_raw']) || '',
    /* When the crown landed. The worklist's crown tier sorts newest first on
     * it; absent on an old snapshot, and then the tier falls back to its date
     * order rather than guessing. */
    championDetectedAt: field(record, ['champion_detected_at']) || '',
    championSearchTag: field(record, ['sr_champion_search']) || '',
    /* WHEN it was hunted and HOW MANY TIMES — both already on the record and on
     * the backend's read (`hubspot.js#CHAIN_PROPS`), and neither was being
     * carried through. Without them a record nobody can find is indistinguishable
     * from one nobody has tried since, which is exactly the distinction the
     * Hunted tab exists to make. Attempts is a NUMBER: `0` and "never searched"
     * must not both read as falsy-and-therefore-blank. */
    championSearchedAt: field(record, ['champion_searched_at']) || '',
    championSearchAttempts: Number(field(record, ['champion_search_attempts']) || 0) || 0,
    championSearchedBy: field(record, ['champion_searched_by']) || '',

    // Incumbent competitor — the vendor currently holding this championship's
    // ring/award business. Backed by the first-class HubSpot property
    // `sr_incumbent_ring_vendor` on 2-64425303 (label "Incumbent (Ring Vendor)",
    // human-populated; automation never fills or overwrites it). The remaining
    // names are legacy aliases kept so the normalizer degrades gracefully if the
    // backend DTO ever renames the field. `defending_champion` is deliberately
    // LAST — it is a different concept (last year's winner, not the vendor) and
    // is only a display fallback.
    incumbent:
      field(record, [
        'sr_incumbent_ring_vendor',
        'sr_incumbent_competitor',
        'incumbent_competitor',
        'sr_incumbent',
        'sr_defending_champion',
        'defending_champion',
      ]) || '',
    incumbentConfidence: field(record, ['sr_incumbent_confidence']) || '',
    incumbentSource: field(record, ['sr_incumbent_source']) || '',

    // $ (for Pipeline TAM when a value field is present on the record)
    value: numOrNull(field(record, ['sr_deal_amount', 'amount', 'sr_estimated_value', 'sr_tam'])),

    key: field(record, ['sr_championship_key']) || '',
    /* Stamped once here so every view reads the same answer — see isRetired.
     * `name` is already resolved above, so this reads the normalized name
     * rather than guessing at a raw property. */
    retired: /^RETIRED\s*-\s*/i.test(String(name || '')),
    /* ⭐ THE SCOPE RULING, stamped for the same reason `retired` is: so no
     * view has to know the property name or the exact spelling of the value.
     * `scopeStatus` is carried verbatim rather than as a boolean, because
     * three states are meaningful — Out of Scope, In Scope, and BLANK meaning
     * nobody has ruled — and a boolean would collapse the last two. */
    scopeStatus: field(record, ['sr_scope_status']) || '',
    scopeReason: field(record, ['sr_scope_reason']) || '',
    outOfScope: String(field(record, ['sr_scope_status']) || '').trim() === 'Out of Scope',
    raw: record,
  }
}

function numOrNull(v) {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : null
}

function cleanConference(s) {
  return (s == null ? '' : String(s)).replace(/[,;].*$/, '').trim()
}

export function normalizeChampionships(response) {
  const list = Array.isArray(response)
    ? response
    : response?.records ||
      response?.championships ||
      response?.results ||
      response?.data ||
      response?.items ||
      []
  return list.map(normalizeChampionship).filter(Boolean)
}

/* ---- Aggregation --------------------------------------------------- */

export function countByState(championships) {
  const counts = {}
  for (const c of championships) counts[c.state] = (counts[c.state] || 0) + 1
  return counts
}

/**
 * Per-state rollup for the map: count, the stage MOST OF THE STATE IS IN,
 * whether any record is crowned, and the recorded-amount sum.
 *
 * ⚠️ `dominant` WAS A MAX, NOT A DISTRIBUTION, AND THAT WAS THE FILL BUG.
 * It used to be built as `if (pm.priority > pursuitMeta(s.dominant).priority)` —
 * the single most advanced record in the state, over a table where 'Champion
 * Crowned' is priority 6, the highest. So a state holding 500 untouched records
 * and ONE crown filled Champion-Crowned green.
 *
 * That is exactly the defect 2.78.0 announced it had fixed. The fix then removed
 * `s.crowned` from `fillFor` and wrote "a boolean must never outrank a
 * distribution" — but the thing it fell through to was never a distribution, so
 * the symptom moved from one field to another and the map kept lying. Both the
 * original and that fix were mine to get right.
 *
 * It is now the MODE, with the margin kept alongside it, so the surface can name
 * the stage AND the count of records in it, rather than implying the whole
 * state. (The 500-untouched-plus-one-crown case above is an illustration of the
 * defect's shape, asserted in verify-map-fill.mjs — not a measured figure from
 * production, which is why it carries no date.) The
 * same shape `dominantRep` has used since 2.78.0 — the two now agree in kind,
 * which they did not before, despite sharing the `dominant` prefix.
 *
 * ⚠️ `priority` SURVIVES ONLY AS A TIE-BREAK, so the map cannot flicker between
 * renders when two stages are level. It no longer selects anything on its own.
 */
export function aggregateByState(championships) {
  const by = {}
  for (const c of championships) {
    const k = c.state
    if (!by[k]) {
      by[k] = {
        code: k, items: [], count: 0, crowned: false, crownedCount: 0, dominant: null,
        /* ⚠️ `tam` IS A SUM OF RECORDED CRM AMOUNTS, NOT THE MODELLED CHAIN.
         * `value` normalizes sr_deal_amount / amount / sr_estimated_value /
         * sr_tam — figures a human typed or a deal carries. It is NOT
         * field size × units per buyer × penetration × price, and must never be
         * labelled "TAM" in the UI as though it were. `valueRecords` is the
         * denominator that makes it readable: hypothetically, a $4M sum over 3
         * of 500 records is a fact about 3 records, not about the state. */
        tam: 0, valueRecords: 0,
        byRep: {}, dominantRep: null, repCount: 0,
        /* The distribution the fill is drawn from, kept so the tooltip can
         * quote it rather than assert a colour nobody can check. */
        byStage: {}, dominantCount: 0, dominantShare: 0, stageCount: 0,
      }
    }
    const s = by[k]
    s.items.push(c)
    s.count++
    if (c.crowned) { s.crowned = true; s.crownedCount++ }
    if (c.value) { s.tam += c.value; s.valueRecords++ }
    /* ⚠️ COUNTED, NOT RANKED. The stage is tallied here and the winner is picked
     * after the loop, from the tally. A record is one vote, never a veto.
     *
     * ⚠️ AND A BLANK STAGE IS ITS OWN BUCKET, not quietly folded into "Not
     * Started": "nobody recorded a status" and "somebody recorded Not Started"
     * are different facts, and the silent-zero rule applies to a stage exactly
     * as it does to a count. */
    const stage = String(c.pursuit ?? '').trim()
    s.byStage[stage] = (s.byStage[stage] || 0) + 1
    /* WHO IS ASSIGNED HERE. Counted per state so the map can be filled by rep
     * rather than by stage — Brian: "make it so the states show color of rep."
     * `ownerName` is the same resolver the tables use, so the map and the rep
     * rollup cannot disagree about who holds a record. */
    const rep = ownerName(c)
    if (rep) s.byRep[rep] = (s.byRep[rep] || 0) + 1
  }
  /* The state's rep is the one holding the most records in it, with the margin
   * kept so the UI can say "mostly" rather than implying sole possession. A tie
   * resolves by name so the map does not flicker between renders. */
  for (const s of Object.values(by)) {
    const entries = Object.entries(s.byRep).sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    s.repCount = entries.length
    s.dominantRep = entries.length ? entries[0][0] : null
    s.dominantRepRecords = entries.length ? entries[0][1] : 0
    s.assignedRecords = entries.reduce((t, [, n]) => t + n, 0)

    /* THE FILL, FROM THE TALLY. Most records first; ties broken by `priority`
     * and then by name, so the same data always paints the same colour. */
    const stages = Object.entries(s.byStage).sort((a, b) =>
      b[1] - a[1]
      || pursuitMeta(b[0]).priority - pursuitMeta(a[0]).priority
      || (a[0] < b[0] ? -1 : 1))
    s.stageCount = stages.length
    s.dominant = stages.length ? stages[0][0] : null
    s.dominantCount = stages.length ? stages[0][1] : 0
    /* ⚠️ THE MARGIN IS THE POINT. A 34% plurality and a 99% majority are the
     * same colour on a map; only this number tells them apart, and the tooltip
     * is required to quote it. */
    s.dominantShare = s.count ? s.dominantCount / s.count : 0
  }
  return by
}

/* ---- REP COLOUR ----------------------------------------------------------
 *
 * Brian: "make it so the states show color of rep."
 *
 * ⚠️ DERIVED FROM THE NAME, NOT A HAND-KEYED TABLE. A fixed map of the nine
 * current reps would give the tenth rep no colour — or worse, silently reuse
 * someone else's, which on a map reads as that person holding a state they have
 * never touched. Hashing the display name means a new rep gets a stable colour
 * the day they are assigned anything, with no code change.
 *
 * The palette is ordered so adjacent indices are far apart in hue, because the
 * failure mode on this surface is two reps that look alike on a phone. */
export const REP_PALETTE = [
  '#1f6f8b', '#b4532a', '#4f7a3f', '#7b4a8c', '#a5762a',
  '#2f6f5f', '#9a3b2f', '#3f5f9a', '#6d7a2a', '#8c4a6a',
  '#2a7a7a', '#7a5a2a',
]

export function repColor(name) {
  const s = String(name || '').trim()
  if (!s || s === NO_OWNER) return 'var(--map-base)'
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return REP_PALETTE[Math.abs(h) % REP_PALETTE.length]
}

/** Sum of per-record value (Pipeline TAM). Null when nothing carries a value. */
export function totalTam(championships) {
  let t = 0, any = false
  for (const c of championships) if (c.value) { t += c.value; any = true }
  return any ? t : null
}

/**
 * ⚠️ COUNT AND VALUE TOGETHER, BECAUSE RANKING BY COUNT ALONE IS WRONG.
 *
 * Brian: ranking reps by record count is wrong by up to 65% — the rep holding
 * the MOST records can rank last by value, and the rep holding the fewest can
 * be near the top. A manager reading a table sorted by `total` is therefore
 * being told the opposite of what they need on the exact screen built to tell
 * them who is carrying what.
 *
 * So `value` and `valueRecords` ride alongside the counts, and the DENOMINATOR
 * travels with the money for the same reason it does on the map tooltip: `value`
 * is sparse on this object, so a sum across a handful of records out of hundreds
 * is a fact about that handful. A reader not told the denominator will take it
 * for everything the rep is assigned. (No figure is quoted here deliberately —
 * I did not re-measure the sparsity, and an undated count is how a stale number
 * gets requoted as today's production state.)
 *
 * ⚠️ THIS IS A SUM OF RECORDED AMOUNTS AND IS NEVER THE MODELLED CHAIN. The TAM
 * chain (field size × units × penetration × price) carries `headlineEligible:
 * false` on a ceiling that rests on an archetype, and lib/tamChain.js honours
 * it. Putting that ceiling beside a person's name is exactly what it is flagged
 * to prevent — a stand-in figure reading as this rep's number. A sum of what is
 * actually on the records is countable, checkable, and is what this returns.
 */
export function repLoad(championships) {
  const m = {}
  for (const c of championships) {
    const k = ownerName(c) || NO_OWNER
    if (!m[k]) m[k] = { rep: k, total: 0, toPursue: 0, won: 0, crowned: 0, value: 0, valueRecords: 0 }
    m[k].total++
    if (c.pursuit === 'To Pursue') m[k].toPursue++
    if (c.pursuit === 'Won') m[k].won++
    if (c.crowned) m[k].crowned++
    const v = Number(c.value)
    if (Number.isFinite(v) && v > 0) { m[k].value += v; m[k].valueRecords++ }
  }
  return Object.values(m).sort((a, b) => b.total - a.total)
}

/**
 * HOW CONCENTRATED AN AREA IS, AND WHETHER IT HAS MOVED.
 *
 * ⚠️ OVERSIGHT IS NOT WORKLOAD. Brian: Jennifer Loveday-Donovan's cheer book is
 * OVERSIGHT — she distributes to Kaitlyn Crews and Abigail Mancuso — so
 * presenting her as personally holding every cheer record misreads the one
 * number a manager would act on. And he asked for the follow-through
 * explicitly: *"I wanna make sure Jennifer is assigning to Kait and Abigail."*
 *
 * ⚠️ NO NAMES ARE HARDCODED HERE. Who oversees what comes from the proficiency
 * map's own `single_point_dependencies`; who they have distributed TO is
 * measured off the live records by counting the OTHER reps holding records in
 * the same area. A hardcoded distributee list is a roster that drifts the first
 * time somebody joins, and the same rule already keeps `repWorklist.js` free of
 * names. A stalled book then reads as a fact — the area is still entirely one
 * person's — rather than as an accusation.
 *
 * `matcher` decides membership in the area; the caller supplies it so this
 * function never has to know what "cheer" means.
 */
export function areaConcentration(championships, matcher) {
  const inArea = championships.filter((c) => matcher(c))
  const byRep = new Map()
  for (const c of inArea) {
    const k = ownerName(c) || NO_OWNER
    byRep.set(k, (byRep.get(k) || 0) + 1)
  }
  const holders = [...byRep.entries()]
    .filter(([rep]) => rep !== NO_OWNER)
    .sort((a, b) => b[1] - a[1])
    .map(([rep, count]) => ({ rep, count }))
  const assigned = holders.reduce((n, h) => n + h.count, 0)
  return {
    inArea: inArea.length,
    unassigned: byRep.get(NO_OWNER) || 0,
    holders,
    /* The share held by the single largest holder. 100 means nothing has been
     * distributed; a figure meaningfully under it means it has. */
    topShare: assigned ? Math.round((holders[0]?.count || 0) / assigned * 100) : 0,
    distributedTo: holders.slice(1),
  }
}

export function activeReps(championships) {
  const s = new Set()
  for (const c of championships) { const o = ownerName(c); if (o) s.add(o) }
  return s.size
}

/* ---- Time windows -------------------------------------------------- */

export const TIME_WINDOWS = [
  { key: 'all', label: 'All' },
  { key: 'next30', label: 'Next 30', dir: 'future', days: 30 },
  { key: 'next60', label: 'Next 60', dir: 'future', days: 60 },
  { key: 'next90', label: 'Next 90', dir: 'future', days: 90 },
  { key: 'next365', label: 'Next 12mo', dir: 'future', days: 365 },
  { key: 'past', label: 'Previous', dir: 'past' },
]

export function inWindow(championship, windowKey, now = new Date()) {
  if (windowKey === 'all') return true
  const w = TIME_WINDOWS.find((x) => x.key === windowKey)
  if (!w) return true
  // Use the EFFECTIVE date, not the raw event date: ~7,586 records carry a
  // skewed 2027 sr_event_date, so windowing on the raw value under-counts
  // upcoming championships. effDate is the reconciled, sane date.
  const d = championship.effDate
  if (!d) return false
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  if (w.dir === 'past') return d < start
  const end = new Date(start)
  end.setDate(end.getDate() + w.days)
  return d >= start && d <= end
}

export function filterChampionships(championships, opts = {}) {
  const { state, window, pursuit, rep, sport, level, conference, season, query, now } = opts
  const q = String(query || '').trim().toLowerCase()
  return championships.filter((c) => {
    if (state && c.state !== state) return false
    if (window && !inWindow(c, window, now)) return false
    if (pursuit && c.pursuit !== pursuit) return false
    if (rep && ownerName(c) !== rep) return false
    if (sport && c.sport !== sport) return false
    if (level && c.level !== level) return false
    if (conference && c.conference !== conference) return false
    if (season && String(c.season) !== String(season)) return false
    if (q) {
      const hay = `${c.name} ${c.champion} ${c.winningSchool} ${c.conference} ${ownerName(c)} ${c.city}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

export function sortByDate(championships, ascending = true) {
  return [...championships].sort((a, b) => {
    if (!a.effDate && !b.effDate) return 0
    if (!a.effDate) return 1
    if (!b.effDate) return -1
    return ascending ? a.effDate - b.effDate : b.effDate - a.effDate
  })
}

export function uniqueValues(championships, keyFn) {
  return [...new Set(championships.map(keyFn).filter(Boolean))].sort()
}

/**
 * ⚠️ THIS TOOK THE WHOLE COMMAND CENTER DOWN, and the reason is worth keeping.
 *
 * It used to call `dateObj.toLocaleDateString(...)` directly — correct for the
 * normalizer's Date fields (`effDate`, `heldDate`, `dateObj`) and fatal for
 * everything else. `HuntedBoard` passes `championSearchedAt`, which the
 * normalizer keeps as a RAW STRING off `champion_searched_at`. Clicking Hunted
 * threw `e.toLocaleDateString is not a function` and blanked the page.
 *
 * It was never one bug. Roughly thirty callers pass a field of this kind —
 * `hero.nominatedAt`, `partnership.signedAt`, `lastTitleAt`, `crownedAt`,
 * `openDate`, `pursuitDate`, `wonDate` — and each was one raw string away from
 * doing the same thing to whichever view rendered it. Coercing HERE closes all
 * of them at once; fixing the single call site would have left the mine field.
 *
 * `parseDate` also carries the rules the rest of the app already agrees on:
 * epoch-ms, `YYYY-MM-DD`, and the pre-1971 sentinel that means "no date" rather
 * than 1970. A Date instance passes through untouched, so nothing that works
 * today changes.
 */
export function formatDate(value) {
  if (!value) return 'Date TBD'
  const d = value instanceof Date ? value : parseDate(value)
  /* An unparseable value returns the same words as an absent one. Rendering a
   * raw string here would be worse than the crash — the board would show
   * "2026-08-14T00:00:00Z" beside real dates and read as data, not as a fault. */
  if (!d || Number.isNaN(d.getTime())) return 'Date TBD'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Human countdown from a days_to_event integer (null → "no date"). */
export function formatCountdown(days) {
  if (days == null) return 'no date'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  return days > 0 ? `in ${days}d` : `${Math.abs(days)}d ago`
}

/* ---- Date KIND ------------------------------------------------------
 * Not every space counts down to an event. An RFP counts down to its CLOSE
 * date — the submission deadline — and "in 5d / 5d ago" reads wrong for a
 * deadline that can lapse. A record may therefore declare a `dateKind`; a
 * championship record carries none, so both helpers below are the identity
 * for it and the championship surface is unchanged. */

/** What this record's effective date IS ("Event date" unless it says otherwise). */
export function dateLabelOf(c) {
  return (c && c.dateLabel) || 'Event date'
}

/** Countdown phrased for the record's date kind. Falls back to formatCountdown. */
export function formatCountdownFor(c, days) {
  if (c?.dateKind !== 'close') return formatCountdown(days)
  if (days == null) return 'no close date'
  if (days === 0) return 'closes today'
  if (days === 1) return 'closes tomorrow'
  return days > 0 ? `closes in ${days}d` : `closed ${Math.abs(days)}d ago`
}

/* ==================================================================
   THE ORGANIZING SPINE: days_to_event → time_bucket
   ================================================================== */

const DAY_MS = 86400000
function startOfDay(now) { const d = new Date(now); d.setHours(0, 0, 0, 0); return d }

/** days_to_event = EFFECTIVE date − today (null when no usable date). */
export function daysToEvent(c, now = new Date()) {
  const d = c.effDate
  if (!d) return null
  return Math.round((startOfDay(d) - startOfDay(now)) / DAY_MS)
}

/**
 * Days since it was HELD — positive for the past, negative for the future.
 *
 * The look-back reads this and the look-forward reads daysToEvent, because they
 * are asking different questions of the same record. `heldDate` is only absent
 * on records normalized before it existed, and falling back to effDate there
 * keeps them visible rather than silently dropping them from every bucket.
 */
export function daysSinceHeld(c, now = new Date()) {
  const d = c.heldDate || c.effDate
  if (!d) return null
  return Math.round((startOfDay(now) - startOfDay(d)) / DAY_MS)
}

/**
 * The bucket enum — a REAL rolling 30/60/90 look-back + look-forward window,
 * every edge measured in days from today off the EFFECTIVE date. Listed in
 * chronological order (oldest → farthest out → undated).
 *
 * `side`/`span` describe the rolling window each bucket closes out, so the UI
 * can build cumulative "Last 30 / 60 / 90" and "Next 30 / 60 / 90" lenses
 * without re-deriving the edges. EARLIER / LATER catch everything beyond ±90
 * and are labelled as such — they are never hidden.
 */
export const TIME_BUCKETS = [
  { key: 'EARLIER', label: `Earlier than ${LOOKBACK_DAYS} days ago`, side: 'back', tone: 'muted', min: -Infinity, max: -(LOOKBACK_DAYS + 1) },
  { key: 'BACK_90', label: '61–90 days ago', side: 'back', span: 90, tone: 'amber', min: -90, max: -61 },
  { key: 'BACK_60', label: '31–60 days ago', side: 'back', span: 60, tone: 'crimson', min: -60, max: -31 },
  { key: 'BACK_30', label: '8–30 days ago', side: 'back', span: 30, tone: 'crimson', min: -30, max: -8 },
  { key: 'BACK_7', label: 'Last 7 days', side: 'back', span: 7, tone: 'crimson', min: -7, max: -1 },
  { key: 'NEXT_7', label: 'Next 7 days', side: 'fwd', span: 7, tone: 'crimson', min: 0, max: 7 },
  { key: 'NEXT_30', label: '8–30 days out', side: 'fwd', span: 30, tone: 'crimson', min: 8, max: 30 },
  { key: 'NEXT_60', label: '31–60 days out', side: 'fwd', span: 60, tone: 'amber', min: 31, max: 60 },
  { key: 'NEXT_90', label: '61–90 days out', side: 'fwd', span: 90, tone: 'brass', min: 61, max: 90 },
  { key: 'LATER', label: `Beyond ${LOOKAHEAD_DAYS} days out`, side: 'fwd', tone: 'info', min: LOOKAHEAD_DAYS + 1, max: Infinity },
  { key: 'NO_DATE', label: 'No date', tone: 'muted' },
]
export const BUCKET_BY_KEY = Object.fromEntries(TIME_BUCKETS.map((b) => [b.key, b]))
// Near-term default selection for worklists: the ±7/30 core of the lens.
export const NEAR_TERM_BUCKETS = ['BACK_7', 'BACK_30', 'NEXT_7', 'NEXT_30']

export function timeBucket(c, now = new Date()) {
  /* PAST BUCKETS READ THE HELD DATE, FUTURE BUCKETS READ THE NEXT OCCURRENCE.
   *
   * Both derive from the same record and they disagree on purpose. Keying every
   * bucket off effDate is what left EARLIER permanently empty: an estimated
   * date rolls forward at 90 days, so nothing could ever be "earlier than 90
   * days ago" no matter how long ago it actually happened. */
  const since = daysSinceHeld(c, now)
  if (since != null && since >= 1) {
    const d = -since
    for (const b of TIME_BUCKETS) {
      if (b.min == null || b.side !== 'back') continue
      if (d >= b.min && d <= b.max) return b.key
    }
    return 'EARLIER'
  }
  const d = daysToEvent(c, now)
  if (d == null) return 'NO_DATE'
  for (const b of TIME_BUCKETS) {
    if (b.min == null) continue
    if (d >= b.min && d <= b.max) return b.key
  }
  // Unreachable — EARLIER/LATER are open-ended — but never drop a record.
  return d < 0 ? 'EARLIER' : 'LATER'
}

/**
 * Is this record inside a rolling window? `side` = 'back' (concluded within the
 * last `span` days) or 'fwd' (upcoming within the next `span` days, inclusive of
 * today). This is what the 30/60/90 lens pills filter on — cumulative by design:
 * "Last 60" contains "Last 30".
 */
export function inRollingWindow(c, side, span, now = new Date()) {
  if (side === 'back') {
    // Held date, not next occurrence — see timeBucket.
    const since = daysSinceHeld(c, now)
    return since != null && since >= 1 && since <= span
  }
  const d = daysToEvent(c, now)
  if (d == null) return false
  return d >= 0 && d <= span
}

/**
 * Everything concluded longer ago than the lens reaches.
 *
 * This is the population the board could never show — and the one the backtest
 * works. It is a first-class view rather than a filter people have to think to
 * apply, because "we are missing the majority of the championships" was a
 * report about a bucket that structurally could not fill.
 */
export function concludedBefore(championships, span = LOOKBACK_DAYS, now = new Date()) {
  return championships.filter((c) => {
    const since = daysSinceHeld(c, now)
    return since != null && since > span
  })
}

/**
 * Cumulative counts for the look-back + look-forward lens, plus the
 * out-of-window tails. Keyed off WINDOW_STEPS, so `back7`/`back30`/… appear
 * automatically for whatever steps the lens declares — the pill bar and the
 * bucket edges can never disagree about what "Last 7" means.
 *
 * Counts are CUMULATIVE (back7 ⊆ back30 ⊆ back60 ⊆ back90), which is how people
 * read a 7/30/60/90 lens. The tails are exclusive, so the conservation identity
 * still holds and proves nothing is hidden:
 *   backMax + nextMax + earlier + later + noDate === total
 */
export function windowCounts(items, now = new Date()) {
  const c = { earlier: 0, later: 0, noDate: 0, total: 0 }
  for (const n of WINDOW_STEPS) { c[`back${n}`] = 0; c[`next${n}`] = 0 }
  for (const it of items) {
    c.total++
    /* PAST OFF THE HELD DATE, FUTURE OFF THE NEXT OCCURRENCE — the same split
     * as timeBucket. `earlier` used to be near-zero not because there was
     * nothing there but because a stale estimate rolled forward before it could
     * ever be counted. It now reports the real tail, which is most of the
     * universe. */
    const since = daysSinceHeld(it, now)
    if (since != null && since >= 1) {
      if (since > LOOKBACK_DAYS) { c.earlier++; continue }
      for (const n of WINDOW_STEPS) if (since <= n) c[`back${n}`]++
      continue
    }
    const d = daysToEvent(it, now)
    if (d == null) { c.noDate++; continue }
    if (d < 0) {
      // Held today-ish but the next occurrence already passed: not the past by
      // the held-date reading, so it belongs to the nearest look-back step
      // rather than being dropped.
      for (const n of WINDOW_STEPS) if (d >= -n) c[`back${n}`]++
      continue
    }
    if (d > LOOKAHEAD_DAYS) { c.later++; continue }
    for (const n of WINDOW_STEPS) if (d <= n) c[`next${n}`]++
  }
  return c
}

/** Group championships by time_bucket → { key: [items sorted by days asc] }. */
export function groupByBucket(championships, now = new Date()) {
  const g = {}
  for (const b of TIME_BUCKETS) g[b.key] = []
  for (const c of championships) g[timeBucket(c, now)].push(c)
  for (const k of Object.keys(g)) {
    g[k].sort((a, b) => {
      const da = daysToEvent(a, now), db = daysToEvent(b, now)
      if (da == null) return 1
      if (db == null) return -1
      return da - db
    })
  }
  return g
}

/* ==================================================================
   PURSUIT STAGES — the primary board lens.

   The SAME rolling 7/30/60/90 look-back / look-forward windows as TIME_BUCKETS,
   re-ordered for action (what just concluded, then what's coming) and dressed
   with board colors/hints. Keying is purely off the EFFECTIVE date: a crowned
   record no longer jumps into "Just Concluded" regardless of when it happened
   — that was what put two-year-old events at the top of the board.

   Nothing here is a hidden bucket. `primary` only controls whether a stage
   gets its own summary chip; every stage renders in the table.
   ================================================================== */

export const PURSUIT_STAGES = [
  { key: 'BACK_7', label: 'Just Concluded', side: 'back', span: 7, color: '#9a3b2f', primary: true, hint: 'Concluded in the last 7 days — capture the winner' },
  { key: 'NEXT_7', label: 'Imminent', side: 'fwd', span: 7, color: '#c0562e', primary: true, hint: 'Next 7 days' },
  { key: 'NEXT_30', label: 'Closing', side: 'fwd', span: 30, color: '#a5762a', primary: true, hint: '8–30 days out' },
  { key: 'NEXT_60', label: 'Active', side: 'fwd', span: 60, color: '#4f7a3f', primary: true, hint: '31–60 days out' },
  { key: 'NEXT_90', label: 'Horizon', side: 'fwd', span: 90, color: '#3f7a6a', primary: true, hint: '61–90 days out' },
  { key: 'BACK_30', label: 'Concluded 8–30 days ago', side: 'back', span: 30, color: '#8a4033', primary: false, hint: 'Look-back · 30-day window' },
  { key: 'BACK_60', label: 'Concluded 31–60 days ago', side: 'back', span: 60, color: '#7a4638', primary: false, hint: 'Look-back · 60-day window' },
  { key: 'BACK_90', label: 'Concluded 61–90 days ago', side: 'back', span: 90, color: '#6d5642', primary: false, hint: 'Look-back · 90-day window' },
  { key: 'LATER', label: `Later — beyond ${LOOKAHEAD_DAYS} days out`, side: 'fwd', color: '#3f6f9a', primary: false, hint: 'Outside the look-forward window — fully browsable' },
  { key: 'EARLIER', label: `Earlier — more than ${LOOKBACK_DAYS} days ago`, side: 'back', color: '#6b6459', primary: false, hint: 'Outside the look-back window — fully browsable' },
  { key: 'NO_DATE', label: 'No date', color: '#8a8272', primary: false, hint: 'Undated — needs a date' },
]
export const STAGE_BY_KEY = Object.fromEntries(PURSUIT_STAGES.map((s) => [s.key, s]))
const STAGE_ORDER = Object.fromEntries(PURSUIT_STAGES.map((s, i) => [s.key, i]))

/**
 * The pursuit stage for one record — the rolling window its EFFECTIVE date
 * falls in. Identical keys to `timeBucket`, so the two lenses can never drift.
 */
export function pursuitStage(c, now = new Date()) {
  return timeBucket(c, now)
}
export function stageMeta(key) {
  return STAGE_BY_KEY[key] || { key, label: key, color: 'var(--faint)', primary: false }
}

/** Group championships by pursuit stage → { key: [items] }. */
export function groupByStage(championships, now = new Date()) {
  const g = {}
  for (const s of PURSUIT_STAGES) g[s.key] = []
  for (const c of championships) g[pursuitStage(c, now)].push(c)
  return g
}

/* ==================================================================
   THE STAGE TALLY — the summary bar's arithmetic, DERIVED.

   ⚠️ THE DEFECT THIS EXISTS TO KILL (audit 2026-09-11, P1).

   The board's summary bar rendered `PURSUIT_STAGES.filter(s => s.primary)` —
   five chips — beside a hand-written note reading
   "Outside ±90d: N · no date: M — all listed below". Read together those seven
   numbers look exactly like a partition of the universe, and they are not:
   BACK_30 / BACK_60 / BACK_90 are `primary: false`, so they got no chip and
   their records were counted nowhere in the summary.

   On the live object that shortfall was 203 records — and every one of them is
   an event that concluded 8–90 days ago: the ring-sale window. The TABLE below
   rendered them correctly the whole time (all 11 groups, summing to 14,805
   exactly). But an auditor driving the live site read the summary, found it
   203 short, and concluded three buckets had no render group at all. A summary
   that silently omits a stage does not just under-report — it actively teaches
   the reader that the rows are missing.

   So the bar is no longer a hand-picked subset. `stageTally` walks EVERY stage
   the model declares and reports what it could not place, which makes
   "the chips add up to the universe" a checkable property instead of a
   sentence somebody typed once. A stage added to PURSUIT_STAGES appears in the
   summary the day it is added; a group key the stage list does not know about
   is reported as `stray` rather than silently dropped.
   ================================================================== */
export function stageTally(groups, total = null) {
  const rows = PURSUIT_STAGES.map((s) => ({ ...s, count: (groups?.[s.key] || []).length }))
  const accounted = rows.reduce((n, r) => n + r.count, 0)
  /* A key in `groups` that no stage claims. Structurally unreachable while
   * groupByStage and PURSUIT_STAGES share their key list — which is the point:
   * if that ever stops being true, this reports it instead of losing rows. */
  const stray = Object.keys(groups || {})
    .filter((k) => !STAGE_BY_KEY[k])
    .map((k) => ({ key: k, count: groups[k].length }))
    .filter((r) => r.count > 0)
  const expected = total == null ? accounted : total
  return {
    rows,
    primary: rows.filter((r) => r.primary),
    rest: rows.filter((r) => !r.primary),
    accounted,
    total: expected,
    stray,
    // The assertion the footer prints. True means every record in view landed
    // in exactly one rendered stage.
    reconciled: accounted === expected && stray.length === 0,
    missing: expected - accounted,
  }
}

/**
 * Does the stage list cover every key the bucket model can emit?
 *
 * `pursuitStage` IS `timeBucket`, so any bucket key without a matching stage is
 * a record the board computes and then has nowhere to draw. Asserted directly
 * by verify-windows.mjs rather than trusted.
 */
export function stagesCoverBuckets() {
  const missing = TIME_BUCKETS.map((b) => b.key).filter((k) => !STAGE_BY_KEY[k])
  const extra = PURSUIT_STAGES.map((s) => s.key).filter((k) => !BUCKET_BY_KEY[k])
  return { ok: missing.length === 0 && extra.length === 0, missing, extra }
}

/** Sort comparator for the board columns. Blanks / undated always sort last. */
export function makeComparator(field, dir = 'asc', now = new Date()) {
  const mul = dir === 'desc' ? -1 : 1
  const strKey = {
    sport: 'sport', level: 'level', segment: 'segment', name: 'name', incumbent: 'incumbent',
    // Every view can sort on these too, so the tabs never disagree.
    state: 'state', pursuit: 'pursuit', conference: 'conference',
    // Pursuit-space dimension. `product` is what an RFP is FOR
    // (sr_rfp_category); blank on a championship, which sorts last as usual.
    product: 'product',
    // Employee-recognition dimensions (the Corporate space). Blank on every
    // other record, which sorts last exactly like any other missing value.
    industry: 'industry',
    hqLocation: 'hqLocation',
    // Organization spaces: what kind of body it is ("National Organization" /
    // "Greek Chapter" / "Police Department"). Blank elsewhere → sorts last.
    orgType: 'orgType',
  }
  return (a, b) => {
    if (field === 'owner') {
      // Blank owners are the expected state right now — always sort last.
      const va = ownerName(a).toLowerCase(), vb = ownerName(b).toLowerCase()
      if (!va && !vb) return 0
      if (!va) return 1
      if (!vb) return -1
      return va < vb ? -1 * mul : va > vb ? 1 * mul : 0
    }
    // Every DATE column sorts the same way, blanks last: the effective date, the
    // RFP's surfaced date, and the Greek pursuit's opened / won dates. A record
    // with no pursuit deal has neither, which sorts last exactly like any other
    // missing value — it is never treated as "today" or as the epoch.
    if (field === 'date' || field === 'openDate' || field === 'pursuitDate' || field === 'wonDate') {
      const key = field === 'date' ? 'effDate' : field
      const da = a[key] ? a[key].getTime() : null
      const db = b[key] ? b[key].getTime() : null
      if (da == null && db == null) return 0
      if (da == null) return 1
      if (db == null) return -1
      return (da - db) * mul
    }
    // THE COMMUNITY HERO COLUMN. Departments that HAVE a hero sort together
    // (alphabetically by the nomination's status), and the ones that don't sort
    // last — which is the useful end of it: the gap is the worklist.
    if (field === 'heroStatus') {
      const va = a?.hero ? String(a.hero.status || a.hero.stage || 'nominated').toLowerCase() : ''
      const vb = b?.hero ? String(b.hero.status || b.hero.stage || 'nominated').toLowerCase() : ''
      if (!va && !vb) return 0
      if (!va) return 1
      if (!vb) return -1
      return va < vb ? -1 * mul : va > vb ? 1 * mul : 0
    }
    if (field === 'stage') {
      return ((STAGE_ORDER[pursuitStage(a, now)] ?? 9) - (STAGE_ORDER[pursuitStage(b, now)] ?? 9)) * mul
    }
    // THE CHEER GYM'S CHAMPIONSHIP FOOTPRINT — how many titles the gym's teams
    // have won, aggregated across all of them, and how many of its teams have
    // ever won one. Brian's ask: surface the gyms with the strongest
    // championship relationships first.
    //
    // ZERO IS A REAL ANSWER here, not a blank: a gym with no titles is a real,
    // sortable state (and `desc` is what a rep actually wants — most titles
    // first). Only a record that carries no footprint at all — a championship,
    // an RFP, a police department — sorts last, exactly like any other missing
    // value.
    if (field === 'titles' || field === 'teamCount') {
      const key = field === 'titles' ? 'titleCount' : 'teamCount'
      const na = typeof a[key] === 'number' ? a[key] : null
      const nb = typeof b[key] === 'number' ? b[key] : null
      if (na == null && nb == null) return 0
      if (na == null) return 1
      if (nb == null) return -1
      return (na - nb) * mul
    }
    // THE PARTNERSHIP PIPELINE — target → in pursuit → contract signed →
    // partner, in pipeline order rather than alphabetically, so sorting by it
    // walks the funnel. Any record with no partnership state sorts last.
    //
    // The rank is STAMPED ON THE RECORD by the cheer rollup rather than derived
    // here: this file is the shared library every space depends on, and it must
    // not have to know what a cheer partnership is.
    if (field === 'partnership') {
      const oa = typeof a?.partnershipRank === 'number' ? a.partnershipRank : null
      const ob = typeof b?.partnershipRank === 'number' ? b.partnershipRank : null
      if (oa == null && ob == null) return 0
      if (oa == null) return 1
      if (ob == null) return -1
      return (oa - ob) * mul
    }
    // HEADCOUNT — a real numeric sort, not a lexical one, so 900 doesn't come
    // after 10,000. An unknown headcount is null and always sorts last, the
    // same rule every other blank follows here.
    if (field === 'employees') {
      const na = typeof a.employees === 'number' ? a.employees : null
      const nb = typeof b.employees === 'number' ? b.employees : null
      if (na == null && nb == null) return 0
      if (na == null) return 1
      if (nb == null) return -1
      return (na - nb) * mul
    }
    const k = strKey[field] || 'name'
    const va = String(a[k] || '').toLowerCase()
    const vb = String(b[k] || '').toLowerCase()
    if (!va && !vb) return 0
    if (!va) return 1
    if (!vb) return -1
    if (va < vb) return -1 * mul
    if (va > vb) return 1 * mul
    return 0
  }
}

/* ---- Honest gap detection ------------------------------------------ */

/* ====================================================================== *
 * THE DATE STAMP — "this date was hunted down, not guessed"
 *
 * Brian: "Next to any championship you have officially hunted down with
 * official date, I want there to be a stamp on it."
 *
 * The stamp is the visible half of the date programme. Today 920 of 10,556
 * fiscal-year records carry a date read off a governing body's own published
 * calendar; the other ~9,600 carry one the SYSTEM derived from a season. Both
 * render as an identical row, so a rep has no way to tell whether the date
 * driving a countdown is a fact or an estimate.
 *
 * ⚠️ `dateLocked` IS NOT THE TEST, AND USING IT WOULD PUT THE STAMP ON A LIE.
 * 154 records carry `sr_date_status = 'Confirmed'` and NO `sr_event_date` at
 * all — a broken state left by an earlier import. They pass a naive
 * status check, they fire nothing, and stamping them would put "SOURCED" beside
 * a blank date. So the stamp requires BOTH a Confirmed status AND a real date,
 * and the broken combination is reported as its own thing rather than swept in.
 *
 * ⚠️ NOTHING IS STAMPED "PROJECTED". A badge on ~90% of rows is wallpaper: it
 * stops being read within a day and makes the board noisier without making it
 * more honest. The stamp is rare on purpose — its meaning comes from being
 * unusual, and it becomes the norm only as the sourcing work lands.
 * ====================================================================== */

/**
 * @returns {{kind:'sourced'|'unsourced', label:string, title:string}|null}
 *   `sourced`   — stamp it. A governing body published this date.
 *   `unsourced` — a warning, NOT a stamp: marked Confirmed with no date.
 *   `null`      — an ordinary projected/undated record. Draw nothing.
 */
export function dateStamp(c) {
  const status = String(c?.dateStatus || '').trim()
  if (status !== 'Confirmed') return null
  /* `effDate` is the date every countdown and bucket actually uses, so it is
   * the right thing to require — a record can carry a raw value that does not
   * parse, and that is not a date anybody can act on. */
  if (c?.effDate) {
    return {
      kind: 'sourced',
      label: 'SOURCED',
      title: 'Official date — read from this governing body’s own published championship calendar, not derived from a season.',
    }
  }
  return {
    kind: 'unsourced',
    label: 'NO DATE',
    title: 'Marked Confirmed but carries no event date. It reads as sourced and fires nothing — a broken record, not a sourced one.',
  }
}

export const NO_OWNER = 'Unassigned'

/**
 * THE SENTINEL THAT LETS THE OWNER FILTER SAY "NOBODY".
 *
 * ⚠️ WHY A SENTINEL AND NOT `''`. Brian: "If something is unassigned - i need
 * to know about it. we must be able to assign it." He could not, and the reason
 * was one line in CommandCenter.jsx:
 *
 *     if (filters.owner && ownerName(c) !== filters.owner) return false
 *
 * The value that selects unassigned records is `''` — which the SAME line reads
 * as "no filter at all". So the one cohort nobody owns was the one cohort the
 * board could not be asked for, and 2,399 records (measured live 2026-10-13 off
 * the served snapshot, 15,814 total) were reachable only by writing a query.
 * CommandCenter.jsx said so in a comment and shipped anyway:
 * "THE OWNER FILTER CANNOT EXPRESS 'UNASSIGNED'."
 *
 * A sentinel fixes it without touching the falsy-means-no-filter convention
 * every other dimension depends on. It is a module constant rather than a
 * literal in two files because a filter whose two halves can drift apart is a
 * filter that silently selects nothing — and selecting nothing here looks
 * exactly like "everything is assigned", which is the wrong direction of error.
 *
 * It cannot collide with a rep name: `assignableReps` is a closed roster of
 * human names and the backend refuses an off-roster value outright.
 */
export const UNASSIGNED_FILTER = '__sr_unassigned__'

// "Covered" pursuit = worked beyond the default. Everything else is a gap.
const UNWORKED_PURSUIT = new Set(['', 'Not Started', 'To Pursue'])

// Values HubSpot/the normalizer can leave behind that all mean "nobody".
const BLANK_OWNER = /^(unassigned|unknown|none|null|undefined|n\/?a|-|—)$/i

/**
 * Display name for a record's owner/rep, or '' when there isn't one.
 *
 * Reps are being unassigned in HubSpot, so blank is the EXPECTED state — this
 * must never throw and must never invent a name. It coerces whatever the DTO
 * carries (string, number, or an owner object) to a safe string so React can
 * always render it, and returns '' for every flavour of "nobody". Callers show
 * "Unassigned" themselves so the empty state is styled as the gap it is.
 */
export function ownerName(c) {
  const raw = c?.rep ?? c?.leadRep ?? ''
  const s =
    raw == null ? ''
      : typeof raw === 'object'
        ? String(raw.name ?? raw.label ?? raw.email ?? raw.fullName ?? '').trim()
        : String(raw).trim()
  return !s || BLANK_OWNER.test(s) ? '' : s
}

export const isNoDate = (c) => !c.effDate
export const isNoOwner = (c) => !ownerName(c)
export const isUncovered = (c) => UNWORKED_PURSUIT.has(String(c.pursuit || '').trim())
export const isNoChampion = (c) => !c.champion && !c.winningSchool
export const isNoState = (c) => !c.state || c.state === NATIONAL
export const isIncumbentUnknown = (c) => !c.incumbent

/**
 * Live gap-bucket counts + nearest-first item lists (the Gaps Board hero).
 *
 * `opts` describes what the SPACE actually has, so a bucket is offered only
 * where it can mean something. Both default to true, so the Championships
 * surface is byte-for-byte what it always was:
 *
 *  - `dated: false` (an organization space — Corporate) drops NO DATE. Every
 *    record there is undated by definition; a bucket holding 100% of the
 *    universe is noise, not an alarm.
 *  - `champions: false` (any non-championship space) drops NO CHAMPION. A
 *    corporation never wins anything, so "winner not captured" is not a gap —
 *    it's a category error.
 *
 * INCUMBENT UNKNOWN needs no flag: it is already scoped to dated records inside
 * ±90 days, so it self-empties in an undated space.
 */
export function gapBuckets(championships, now = new Date(), opts = {}) {
  const { dated = true, champions = true } = opts
  const nearFirst = (list) => sortByDate(list.filter((c) => c.effDate), true).concat(list.filter((c) => !c.effDate))
  const upcoming = championships.filter((c) => { const d = daysToEvent(c, now); return d == null || d >= -1 })
  return [
    dated && { key: 'NO_DATE', label: 'No event date', hint: 'Missing or epoch-null', items: nearFirst(championships.filter(isNoDate)) },
    { key: 'NO_OWNER', label: 'No rep assigned', hint: 'Unassigned rep', items: nearFirst(championships.filter(isNoOwner)) },
    { key: 'UNCOVERED', label: 'Uncovered', hint: 'No status beyond “To Pursue”', items: nearFirst(championships.filter(isUncovered)) },
    champions && { key: 'NO_CHAMPION', label: 'No champion', hint: 'Winner not captured', items: nearFirst(championships.filter(isNoChampion)) },
    { key: 'NO_STATE', label: 'No state / location', hint: 'Geo missing → National', items: nearFirst(championships.filter(isNoState)) },
    { key: 'INCUMBENT_UNKNOWN', label: 'Incumbent unknown', hint: 'Near-term, no incumbent', items: nearFirst(upcoming.filter((c) => { const d = daysToEvent(c, now); return d != null && d <= 90 && isIncumbentUnknown(c) })) },
  ].filter(Boolean)
}

/* ---- Manager rollup ------------------------------------------------ */

export function managerRollup(championships, now = new Date()) {
  const upcoming = championships.filter((c) => { const d = daysToEvent(c, now); return d != null && d >= 0 })
  const ownedAndDated = upcoming.filter((c) => !isNoOwner(c))
  const byLevel = tallyBy(championships, (c) => c.level || '(no level)')
  const bySport = tallyBy(championships, (c) => c.sport || '(no sport)')
  return {
    total: championships.length,
    upcoming: upcoming.length,
    coverage: upcoming.length ? ownedAndDated.length / upcoming.length : 0,
    brokenDates: championships.filter(isNoDate).length, // no usable date at all
    estimatedDates: championships.filter((c) => c.dateConfidence === 'estimated').length,
    confirmedDates: championships.filter((c) => c.dateConfidence === 'confirmed').length,
    noOwner: championships.filter(isNoOwner).length,
    uncovered: championships.filter(isUncovered).length,
    byLevel, bySport,
  }
}

function tallyBy(championships, keyFn) {
  const m = {}
  for (const c of championships) { const k = keyFn(c); m[k] = (m[k] || 0) + 1 }
  return Object.entries(m).map(([k, count]) => ({ k, count })).sort((a, b) => b.count - a.count)
}

/* ---- Pursuit stage columns (for the kanban board) ------------------ */
// The board columns, in workflow order. `unworked` folds empty/Not Started.
export const PURSUIT_COLUMNS = [
  { key: 'To Pursue', label: 'To Pursue' },
  { key: 'Outreach In Progress', label: 'Outreach In Progress' },
  { key: 'Outreach Completed', label: 'Outreach Completed' },
  { key: 'Won', label: 'Won' },
  { key: 'Champion Crowned', label: 'Champion Crowned' },
]
export function pursuitColumn(c) {
  const p = String(c.pursuit || '').trim()
  if (!p || p === 'Not Started') return 'To Pursue'
  return PURSUIT_COLUMNS.some((col) => col.key === p) ? p : 'To Pursue'
}
